"use client";

import { useCallback, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { LucideIcon } from "lucide-react";
import {
  Activity,
  AppWindow,
  Boxes,
  Database,
  HardDrive,
  LoaderCircle,
  Plus,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Workflow,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useRunyiUiProjection } from "./runyi-ui-coordinator";
import { ResponsiveStatusRow } from "@/components/runyi-responsive-status-row";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type CompositionMode = "applications" | "infrastructure" | "bridge";

type ApplicationItem = {
  entity_key: string;
  display_name: string;
};

type ResourceItem = {
  resource_key: string;
  display_name: string;
  resource_type: string;
  provider_key?: string | null;
  tenant_project_ref?: string | null;
  environment_key: string;
  lifecycle_state: string;
  health_state: string;
  immutable: boolean;
  authority_class: string;
};

type Composition = {
  composition_id: string;
  composition_key: string;
  application_entity_key: string;
  environment_key: string;
  revision: number;
  state: string;
  readiness: string;
  requirements?: Array<Record<string, unknown>>;
  resolution_snapshot?: Array<Record<string, unknown>>;
  content_hash: string;
  manifest_id?: string | null;
  desired_state_id?: string | null;
  release_id?: string | null;
  created_at: string;
  updated_at: string;
  metadata?: Record<string, unknown>;
};

type CompositionSlot = {
  slot_id: string;
  composition_id: string;
  slot_key: string;
  logical_capability?: string | null;
  resource_type?: string | null;
  required: boolean;
  reuse_policy: string;
  sharing_policy: string;
  provider_policy: string;
  preferred_providers?: string[];
  state: string;
  resolved_resource_key?: string | null;
  resolved_pool_id?: string | null;
  resolved_profile_key?: string | null;
  assignment_id?: string | null;
  resolution_reason?: string | null;
  metadata?: Record<string, unknown>;
};

type ResourcePool = {
  pool_id: string;
  pool_key: string;
  display_name: string;
  resource_type: string;
  environment_key?: string | null;
  provider_key?: string | null;
  allocation_policy: string;
  lifecycle_state: string;
  health_state: string;
  capacity_policy?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};

type PoolMember = {
  member_id: string;
  pool_id: string;
  resource_key: string;
  member_state: string;
  capacity_weight: number;
  created_at: string;
  updated_at: string;
};

type CostProjection = {
  status?: "COMPLETE" | "PARTIAL" | "UNAVAILABLE";
  model_key?: string;
  display_name?: string;
  provider_key?: string;
  currency?: string;
  projected_monthly_cost?: number;
  gross_list_rate_monthly_cost?: number;
  allocated_allowance_savings?: number;
  catalog_allowance_fraction?: number;
  cost_score?: number;
  confidence?: number;
  missing_inputs?: string[];
  missing_price_metrics?: string[];
  unknown_inputs_fabricated?: boolean;
  shared_allowance_assumed?: boolean;
};

type RecommendationCandidate = {
  candidate_kind?: string;
  candidate_key?: string;
  display_name?: string;
  provider_key?: string;
  resource_key?: string;
  pool_key?: string;
  profile_key?: string;
  resource_type?: string;
  logical_capability?: string;
  evidence_adjusted_score?: number;
  raw_score?: number;
  evidence_coverage_pct?: number;
  confidence?: number;
  evidence?: Record<string, {
    score?: number;
    confidence?: number;
    source?: string;
    derived?: boolean;
    note?: string;
  }>;
  cost_projection?: CostProjection;
  blockers?: string[];
};

type RecommendationSlot = {
  slot: string;
  required: boolean;
  logical_capability?: string | null;
  resource_type?: string | null;
  candidate_count: number;
  recommendation?: RecommendationCandidate | null;
  candidates: RecommendationCandidate[];
};

type RecommendationPreview = {
  application_entity_key: string;
  environment_key: string;
  readiness: string;
  blocked_required_slots: number;
  evidence_coverage_pct: number;
  evidence_quality: "LOW" | "MEDIUM" | "HIGH";
  missing_criteria: string[];
  recommendations: RecommendationSlot[];
  recommended_overrides: {
    provider_preferences?: Record<string, string>;
    resource_preferences?: Record<string, string>;
  };
  content_hash: string;
  advisory_only: boolean;
  automatic_selection_performed: boolean;
  owner_final_choice_required: boolean;
};

type RecommendationSnapshot = {
  recommendation_id: string;
  recommendation_key: string;
  application_entity_key: string;
  environment_key: string;
  revision: number;
  state: string;
  evidence_coverage_pct: number;
  evidence_quality: string;
  created_at: string;
};

type OptimizationEvidence = {
  evidence_id: string;
  target_kind: string;
  target_key: string;
  criterion: string;
  normalized_score: number;
  confidence: number;
  evidence_source: string;
  observed_at: string;
  expires_at?: string | null;
};

type EvidenceSource = {
  source_id: string;
  source_key: string;
  display_name: string;
  provider_key?: string | null;
  source_class: string;
  adapter_key?: string | null;
  connection_key?: string | null;
  lifecycle_state: string;
  health_state: string;
  trust_level: string;
  supported_criteria: string[];
  supported_metrics: string[];
  auto_promote: boolean;
  requires_external_connection: boolean;
  last_ingested_at?: string | null;
  last_success_at?: string | null;
  metadata?: Record<string, unknown>;
};

type EvidenceIngestionRun = {
  ingestion_run_id: string;
  source_key: string;
  status: string;
  discovered_count: number;
  accepted_count: number;
  promoted_count: number;
  rejected_count: number;
  started_at: string;
  completed_at?: string | null;
};

type EvidenceObservation = {
  observation_id: string;
  source_key: string;
  target_kind: string;
  target_key: string;
  provider_key?: string | null;
  metric_key: string;
  numeric_value?: number | null;
  text_value?: string | null;
  unit?: string | null;
  observed_at: string;
  validation_state: string;
  promoted: boolean;
};

type EvidenceActivationAttempt = {
  activation_attempt_id: string;
  source_key: string;
  provider_key?: string | null;
  probe_type: string;
  required_capability?: string | null;
  status: string;
  external_probe_ref?: string | null;
  error_code?: string | null;
  error_message?: string | null;
  started_at: string;
  completed_at?: string | null;
  verification_summary?: Record<string, unknown>;
};

type InfrastructureCostModel = {
  model_key: string;
  provider_key: string;
  target_kind: string;
  target_key: string;
  pricing_source_key: string;
  model_version: string;
  display_name: string;
  currency: string;
  selector?: Record<string, unknown>;
  confidence: number;
  active: boolean;
};

type InfrastructureCostModelComponent = {
  component_id: string;
  model_key: string;
  component_key: string;
  input_key: string;
  price_metric_key: string;
  allowance_metric_key?: string | null;
  unit: string;
  rounding_mode: string;
  required_input: boolean;
  multiplier: number;
  active: boolean;
};

type InfrastructureUsageMetricDefinition = {
  metric_key: string;
  display_name: string;
  metric_domain: string;
  metric_kind: string;
  value_type: string;
  canonical_unit?: string | null;
  default_aggregation: string;
  counter_reset_policy: string;
  cost_input_eligible: boolean;
  high_cardinality_dimensions_allowed: boolean;
  active: boolean;
};

type InfrastructureUsageSource = {
  source_key: string;
  display_name: string;
  provider_key?: string | null;
  source_class: string;
  lifecycle_state: string;
  health_state: string;
  trust_level: string;
  collection_mode: string;
  supported_metrics: string[];
  read_only: boolean;
  last_collection_at?: string | null;
  last_success_at?: string | null;
};

type InfrastructureUsageSeries = {
  series_key: string;
  metric_key: string;
  source_key: string;
  target_kind: string;
  target_key: string;
  provider_key?: string | null;
  application_entity_key?: string | null;
  environment_key?: string | null;
  resource_key?: string | null;
  dimensions?: Record<string, unknown>;
  attribution_method: string;
  attribution_confidence: number;
  last_seen_at: string;
};

type InfrastructureUsageSample = {
  sample_id: number;
  sample_key: string;
  series_key: string;
  observed_at: string;
  numeric_value?: number | null;
  text_value?: string | null;
  json_value?: unknown;
  quality_state: string;
};

type InfrastructureUsageRollup = {
  series_key: string;
  resolution: string;
  bucket_start: string;
  bucket_end: string;
  sample_count: number;
  min_numeric?: number | null;
  max_numeric?: number | null;
  sum_numeric?: number | null;
  avg_numeric?: number | null;
  first_numeric?: number | null;
  last_numeric?: number | null;
  delta_numeric?: number | null;
  reset_detected: boolean;
  quality_state: string;
};

type InfrastructureUsageRetentionPolicy = {
  policy_key: string;
  display_name: string;
  raw_retention_days?: number | null;
  minute_rollup_retention_days?: number | null;
  hour_rollup_retention_days?: number | null;
  day_rollup_retention_days?: number | null;
  month_rollup_retention_days?: number | null;
  archive_after_days?: number | null;
  automatic_deletion_enabled: boolean;
};

type InfrastructureUsageAttributionRule = {
  attribution_rule_id: string;
  rule_key: string;
  source_target_kind: string;
  source_target_key: string;
  metric_key?: string | null;
  application_entity_key: string;
  environment_key?: string | null;
  allocation_fraction: number;
  attribution_method: string;
  confidence: number;
  active: boolean;
};

type InfrastructureCapacityDimensionDefinition = {
  dimension_key: string;
  display_name: string;
  resource_domain: string;
  canonical_unit: string;
  usage_metric_key?: string | null;
  usage_evaluation_mode: string;
  evaluation_period: string;
  capacity_semantics: string;
  warning_utilization_pct: number;
  critical_utilization_pct: number;
  allow_over_capacity: boolean;
  active: boolean;
};

type InfrastructureCapacitySource = {
  source_key: string;
  display_name: string;
  provider_key?: string | null;
  source_class: string;
  lifecycle_state: string;
  health_state: string;
  trust_level: string;
  read_only: boolean;
  supported_dimensions: string[];
  last_collection_at?: string | null;
  last_success_at?: string | null;
};

type InfrastructureCapacityObservation = {
  observation_id: string;
  observation_key: string;
  dimension_key: string;
  source_key: string;
  usage_source_key?: string | null;
  target_kind: string;
  target_key: string;
  provider_key?: string | null;
  application_entity_key?: string | null;
  environment_key?: string | null;
  resource_key?: string | null;
  dimensions?: Record<string, unknown>;
  capacity_value: number;
  observed_at: string;
  effective_from: string;
  expires_at?: string | null;
  quality_state: string;
};

type InfrastructureCapacityRow = {
  row_key: string;
  dimension_key: string;
  display_name: string;
  resource_domain: string;
  canonical_unit: string;
  capacity_semantics: string;
  target_kind: string;
  target_key: string;
  provider_key?: string | null;
  application_entity_key?: string | null;
  environment_key?: string | null;
  resource_key?: string | null;
  dimensions?: Record<string, unknown>;
  capacity_value?: number | null;
  used_value?: number | null;
  remaining_value?: number | null;
  utilization_pct?: number | null;
  headroom_pct?: number | null;
  status: string;
  warning_utilization_pct: number;
  critical_utilization_pct: number;
  capacity_source_key?: string | null;
  capacity_observed_at?: string | null;
  usage_metric_key?: string | null;
  usage?: Record<string, unknown>;
};

type InfrastructureCapacitySnapshot = {
  architecture_version?: string;
  as_of?: string;
  rows?: InfrastructureCapacityRow[];
  row_count?: number;
  provider_mutation_performed?: boolean;
  unknown_values_fabricated?: boolean;
};

type InfrastructureTelemetrySourceBinding = {
  binding_id: string;
  telemetry_key: string;
  signal_class: string;
  source_registry: string;
  source_key: string;
  lifecycle_state: string;
  verification_state: string;
  read_only: boolean;
  active: boolean;
  last_collection_at?: string | null;
  last_success_at?: string | null;
};

type InfrastructureTelemetryVerification = {
  verification_id: string;
  signal_class: string;
  metric_key?: string | null;
  verification_state: string;
  numeric_value?: number | null;
  text_value?: string | null;
  json_value?: unknown;
  canonical_unit?: string | null;
  source_registry: string;
  source_key: string;
  observed_at: string;
  expires_at?: string | null;
};

type InfrastructureTelemetryRow = {
  telemetry_key: string;
  target_kind: string;
  target_key: string;
  resource_key?: string | null;
  provider_key?: string | null;
  provider_account_ref?: string | null;
  provider_project_ref?: string | null;
  resource_type?: string | null;
  environment_key?: string | null;
  region_ref?: string | null;
  plan_ref?: string | null;
  tier_ref?: string | null;
  lifecycle_state: string;
  health_state: string;
  source_health_state?: string | null;
  freshness_state: string;
  identity_verification_state: string;
  stale_after_minutes: number;
  last_observed_at?: string | null;
  last_verified_at?: string | null;
  source_bindings?: InfrastructureTelemetrySourceBinding[];
  latest_verifications?: InfrastructureTelemetryVerification[];
  capacity?: InfrastructureCapacityRow[];
  application_assignments?: Array<{
    application_entity_key: string;
    assignment_slot: string;
    assignment_role: string;
    environment_key: string;
    state: string;
    shared_resource: boolean;
  }>;
  metadata?: Record<string, unknown>;
};

type InfrastructureTelemetrySnapshot = {
  architecture_version?: string;
  as_of?: string;
  rows?: InfrastructureTelemetryRow[];
  row_count?: number;
  provider_mutation_performed?: boolean;
  owner_root_mutated?: boolean;
  unknown_values_fabricated?: boolean;
};

type ExecutionGateRow = {
  gate_id: string;
  operation_id?: string | null;
  provisioning_step_id?: string | null;
  target_key: string;
  action_key: string;
  action_class: string;
  intent_class: string;
  authority_key: string;
  presence_state: string;
  comparison_state: string;
  decision: string;
  execution_directive: string;
  observed_verified: boolean;
  desired_fingerprint?: string | null;
  observed_fingerprint?: string | null;
  discovered_at: string;
  expires_at?: string | null;
  execution_started_at?: string | null;
  execution_completed_at?: string | null;
  final_state?: string | null;
  lock?: {
    lock_id: string;
    status: string;
    fencing_token: number;
    acquired_at: string;
    expires_at: string;
    released_at?: string | null;
  } | null;
};

type ExecutionGateOverview = {
  architecture_version?: string;
  rows?: ExecutionGateRow[];
  row_count?: number;
  active_locks?: number;
  already_satisfied?: number;
  blocked_or_unverified?: number;
  provider_mutation_performed?: boolean;
};

type ProviderTruthContract = {
  contract_key: string;
  provider_key: string;
  adapter_key: string;
  display_name: string;
  collector_kind: string;
  lifecycle_state: string;
  health_state: string;
  trust_level: string;
  read_only: boolean;
  supported_resource_types?: string[];
  supported_signals?: string[];
  last_collection_at?: string | null;
  last_success_at?: string | null;
};

type ProviderTruthRun = {
  run_id: string;
  contract_key: string;
  provider_key: string;
  scope_kind: string;
  scope_ref?: string | null;
  status: string;
  discovered_count: number;
  verified_count: number;
  unknown_count: number;
  bound_count: number;
  telemetry_projected_count: number;
  error_summary?: string | null;
  started_at: string;
  completed_at?: string | null;
};

type ProviderTruthBinding = {
  binding_id: string;
  provider_identity_key: string;
  provider_key: string;
  provider_project_ref?: string | null;
  provider_resource_type: string;
  provider_resource_id: string;
  provider_resource_name?: string | null;
  resource_key: string;
  telemetry_key?: string | null;
  binding_state: string;
  verification_state: string;
  verified_at?: string | null;
};

type ProviderTruthRow = {
  observation_id: string;
  run_id: string;
  contract_key: string;
  provider_identity_key: string;
  provider_key: string;
  adapter_key: string;
  provider_account_ref?: string | null;
  provider_project_ref?: string | null;
  provider_resource_type: string;
  provider_resource_id: string;
  provider_resource_name?: string | null;
  resource_key?: string | null;
  telemetry_key?: string | null;
  presence_state: string;
  lifecycle_state: string;
  health_state: string;
  verification_state: string;
  freshness_state: string;
  region_ref?: string | null;
  plan_ref?: string | null;
  tier_ref?: string | null;
  configuration?: Record<string, unknown>;
  usage?: Record<string, unknown>;
  capacity?: Record<string, unknown>;
  quotas?: Record<string, unknown>;
  cost?: Record<string, unknown>;
  verified_fields?: string[];
  unknown_fields?: string[];
  source_endpoints?: string[];
  observed_at: string;
  expires_at?: string | null;
  contract_lifecycle_state?: string;
  contract_health_state?: string;
  trust_level?: string;
};

type ProviderTruthSnapshot = {
  architecture_version?: string;
  as_of?: string;
  rows?: ProviderTruthRow[];
  row_count?: number;
  resource_identity_authority?: string;
  usage_authority?: string;
  capacity_authority?: string;
  telemetry_authority?: string;
  execution_gate?: string;
  provider_mutation_performed?: boolean;
  unknown_values_fabricated?: boolean;
};

type ProviderMeasurementContract = {
  contract_key: string;
  provider_key: string;
  provider_truth_contract_key?: string | null;
  adapter_key: string;
  display_name: string;
  lifecycle_state: string;
  health_state: string;
  trust_level: string;
  read_only: boolean;
  usage_source_key?: string | null;
  capacity_source_key?: string | null;
  exact_usage_endpoints?: string[];
  exact_capacity_endpoints?: string[];
  supported_metrics?: string[];
  supported_dimensions?: string[];
  last_collection_at?: string | null;
  last_success_at?: string | null;
  metadata?: Record<string, unknown>;
};

type ProviderMeasurementRun = {
  run_id: string;
  contract_key: string;
  provider_key: string;
  scope_kind: string;
  scope_ref?: string | null;
  status: string;
  usage_sample_count: number;
  capacity_observation_count: number;
  telemetry_binding_count: number;
  unknown_count: number;
  error_summary?: string | null;
  source_evidence?: Record<string, unknown>;
  started_at: string;
  completed_at?: string | null;
};

type EvidenceCatalogEntry = {
  catalog_entry_id: string;
  source_key: string;
  catalog_version: string;
  target_kind: string;
  target_key: string;
  provider_key?: string | null;
  metric_key: string;
  numeric_value?: number | null;
  text_value?: string | null;
  unit?: string | null;
  effective_at?: string | null;
  verified_at: string;
  source_url: string;
};

type Overview = {
  architecture_version?: string;
  applications?: ApplicationItem[];
  resources?: ResourceItem[];
  infrastructure_compositions?: Composition[];
  infrastructure_composition_slots?: CompositionSlot[];
  enterprise_resource_pools?: ResourcePool[];
  enterprise_resource_pool_members?: PoolMember[];
  infrastructure_recommendation_snapshots?: RecommendationSnapshot[];
  infrastructure_optimization_evidence?: OptimizationEvidence[];
  infrastructure_evidence_sources?: EvidenceSource[];
  infrastructure_evidence_ingestion_runs?: EvidenceIngestionRun[];
  infrastructure_evidence_observations?: EvidenceObservation[];
  infrastructure_evidence_activation_attempts?: EvidenceActivationAttempt[];
  infrastructure_evidence_catalog_entries?: EvidenceCatalogEntry[];
  infrastructure_cost_models?: InfrastructureCostModel[];
  infrastructure_cost_model_components?: InfrastructureCostModelComponent[];
  infrastructure_usage_metric_definitions?: InfrastructureUsageMetricDefinition[];
  infrastructure_usage_sources?: InfrastructureUsageSource[];
  infrastructure_usage_series?: InfrastructureUsageSeries[];
  infrastructure_usage_samples?: InfrastructureUsageSample[];
  infrastructure_usage_rollups?: InfrastructureUsageRollup[];
  infrastructure_usage_retention_policies?: InfrastructureUsageRetentionPolicy[];
  infrastructure_usage_attribution_rules?: InfrastructureUsageAttributionRule[];
  infrastructure_capacity_dimension_definitions?: InfrastructureCapacityDimensionDefinition[];
  infrastructure_capacity_sources?: InfrastructureCapacitySource[];
  infrastructure_capacity_observations?: InfrastructureCapacityObservation[];
  infrastructure_capacity_utilization?: InfrastructureCapacitySnapshot;
  infrastructure_telemetry_registry?: InfrastructureTelemetryRow[];
  infrastructure_telemetry_source_bindings?: InfrastructureTelemetrySourceBinding[];
  infrastructure_telemetry_verifications?: InfrastructureTelemetryVerification[];
  infrastructure_telemetry_snapshot?: InfrastructureTelemetrySnapshot;
  execution_gate?: ExecutionGateOverview;
  provider_truth_contracts?: ProviderTruthContract[];
  provider_truth_collection_runs?: ProviderTruthRun[];
  provider_resource_truth_bindings?: ProviderTruthBinding[];
  provider_resource_truth?: ProviderTruthSnapshot;
  provider_measurement_contracts?: ProviderMeasurementContract[];
  provider_measurement_collection_runs?: ProviderMeasurementRun[];
};

type CompositionPreview = {
  application_entity_key: string;
  environment_key: string;
  readiness: string;
  blocked_required_slots: number;
  composition_hash: string;
  resolution: Array<{
    slot?: string;
    required?: boolean;
    status?: string;
    logical_capability?: string;
    resource_type?: string;
    provider_key?: string;
    resource_key?: string;
    pool_key?: string;
    profile_key?: string;
    blueprint_key?: string;
    coverage_state?: string;
    reason?: string;
  }>;
};

const DEFAULT_REQUIREMENTS = JSON.stringify([
  {
    slot: "PRIMARY_DATABASE",
    logical_capability: "relational.query",
    resource_type: "DATABASE",
    required: true,
    reuse_policy: "PREFER_EXISTING",
    sharing_policy: "OWNER_CHOICE",
    assignment_role: "PRIMARY",
    source_of_truth: true,
  },
], null, 2);

const DEFAULT_WORKLOAD_PROFILE = JSON.stringify({
  availability_target: "HIGH",
  redundancy: "ROLLBACK_REQUIRED",
  cost_inputs: {},
  cost_normalization: {}
}, null, 2);

const DEFAULT_OBJECTIVE_WEIGHTS = JSON.stringify({
  COST: 15,
  PERFORMANCE: 20,
  REGION: 10,
  REDUNDANCY: 10,
  DATA_AUTHORITY: 15,
  CAPACITY: 10,
  AVAILABILITY: 20,
}, null, 2);

const DEFAULT_OPTIMIZATION_CONSTRAINTS = JSON.stringify({
  min_evidence_coverage_pct: 20,
  allowed_providers: [],
  denied_providers: [],
}, null, 2);

function tone(value?: string | null) {
  const state = String(value ?? "").toUpperCase();
  if (["ACTIVE", "HEALTHY", "READY", "VERIFIED", "EXECUTABLE", "REUSE", "POOL", "FRESH"].includes(state)) {
    return "bg-emerald-400/10 text-emerald-500 dark:text-emerald-300";
  }
  if (["APPLYING", "VERIFYING", "PROVISION", "ASSIGNED", "UNRESOLVED", "WARNING", "FULLY_RESERVED", "STALE"].includes(state)) {
    return "bg-amber-400/10 text-amber-600 dark:text-amber-200";
  }
  if (["BLOCKED", "FAILED", "DEGRADED", "DRIFTED", "RETIRED", "CRITICAL", "OVER_CAPACITY", "EXHAUSTED"].includes(state)) {
    return "bg-red-400/10 text-red-500 dark:text-red-300";
  }
  return "bg-foreground/[0.06] text-foreground/60";
}

function formatCapacityValue(value?: number | null, unit?: string | null) {
  if (value == null || Number.isNaN(Number(value))) return "UNKNOWN";
  const numeric = Number(value);
  const normalizedUnit = String(unit ?? "").toUpperCase();
  if (normalizedUnit === "BYTES") {
    const labels = ["B", "KB", "MB", "GB", "TB", "PB"];
    let scaled = Math.abs(numeric);
    let index = 0;
    while (scaled >= 1024 && index < labels.length - 1) {
      scaled /= 1024;
      index += 1;
    }
    const signed = numeric < 0 ? -scaled : scaled;
    return `${signed.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${labels[index]}`;
  }
  if (normalizedUnit === "PERCENT") {
    return `${numeric.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
  }
  return `${numeric.toLocaleString(undefined, { maximumFractionDigits: 2 })}${normalizedUnit ? ` ${normalizedUnit}` : ""}`;
}

export function InfrastructureCompositionPanel({
  supabase,
  connected,
  mode,
}: {
  supabase: SupabaseClient | null;
  connected: boolean;
  mode: CompositionMode;
}) {
  const uiProjection = useRunyiUiProjection({ supabase, connected, screenKey: "MARKETPLACE" });
  const overview = (uiProjection?.payload?.overview ?? null) as Overview | null;
  const overviewReady = overview !== null;
  const overviewMetric = (value: number | string): number | string =>
    overviewReady ? value : "UNKNOWN";
  const telemetryProjection = (uiProjection?.payload?.telemetry ?? null) as Record<string, unknown> | null;
  const telemetryProjectionReady =
    uiProjection?.payload?.projection_complete === true
    && String(telemetryProjection?.projection_state ?? "").toUpperCase() === "READY";
  const projectedMetric = (key: string): number | string => {
    if (!telemetryProjectionReady) return "UNKNOWN";
    const value = telemetryProjection?.[key];
    return typeof value === "number" && Number.isFinite(value) ? value : "UNKNOWN";
  };
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [applicationEntityKey, setApplicationEntityKey] = useState("");
  const [environmentKey, setEnvironmentKey] = useState("DEV");
  const [requirements, setRequirements] = useState(DEFAULT_REQUIREMENTS);
  const [providerPreferences, setProviderPreferences] = useState("{}");
  const [resourcePreferences, setResourcePreferences] = useState("{}");
  const [policyConstraints, setPolicyConstraints] = useState("{}");
  const [preview, setPreview] = useState<CompositionPreview | null>(null);
  const [recommendation, setRecommendation] = useState<RecommendationPreview | null>(null);
  const [workloadProfile, setWorkloadProfile] = useState(DEFAULT_WORKLOAD_PROFILE);
  const [objectiveWeights, setObjectiveWeights] = useState(DEFAULT_OBJECTIVE_WEIGHTS);
  const [optimizationConstraints, setOptimizationConstraints] = useState(DEFAULT_OPTIMIZATION_CONSTRAINTS);
  const [compositionApprovalId, setCompositionApprovalId] = useState("");
  const [materializeApprovalId, setMaterializeApprovalId] = useState("");

  const [poolKey, setPoolKey] = useState("");
  const [poolName, setPoolName] = useState("");
  const [poolResourceType, setPoolResourceType] = useState("DATABASE");
  const [poolEnvironment, setPoolEnvironment] = useState("DEV");
  const [poolProvider, setPoolProvider] = useState("");
  const [poolAllocationPolicy, setPoolAllocationPolicy] = useState("SHARED_ALLOWED");
  const [poolCapacityPolicy, setPoolCapacityPolicy] = useState("{}");
  const [poolApprovalId, setPoolApprovalId] = useState("");
  const [memberPoolKey, setMemberPoolKey] = useState("");
  const [memberResourceKey, setMemberResourceKey] = useState("");
  const [memberApprovalId, setMemberApprovalId] = useState("");
  const [externalEvidenceApprovalId, setExternalEvidenceApprovalId] = useState("");

  const invoke = useCallback(async (
    operation: string,
    payload: Record<string, unknown>,
    label: string,
  ) => {
    if (!supabase) return null;
    setBusy(label);
    setNotice(null);
    try {
      const { data, error } = await supabase.functions.invoke("runyi-control-tower", {
        body: { operation, ...payload },
      });
      if (error) throw error;
      if (data?.error) throw new Error(String(data.error));
      return data;
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Composition operation failed.");
      return null;
    } finally {
      setBusy(null);
    }
  }, [supabase]);

  const compositions = useMemo(
    () => (overview?.infrastructure_compositions ?? []).slice().sort(
      (a, b) => b.revision - a.revision || b.updated_at.localeCompare(a.updated_at),
    ),
    [overview],
  );
  const slots = overview?.infrastructure_composition_slots ?? [];
  const pools = overview?.enterprise_resource_pools ?? [];
  const members = overview?.enterprise_resource_pool_members ?? [];
  const recommendationSnapshots = overview?.infrastructure_recommendation_snapshots ?? [];
  const optimizationEvidence = overview?.infrastructure_optimization_evidence ?? [];
  const evidenceSources = overview?.infrastructure_evidence_sources ?? [];
  const evidenceRuns = overview?.infrastructure_evidence_ingestion_runs ?? [];
  const evidenceObservations = overview?.infrastructure_evidence_observations ?? [];
  const evidenceActivationAttempts = overview?.infrastructure_evidence_activation_attempts ?? [];
  const evidenceCatalogEntries = overview?.infrastructure_evidence_catalog_entries ?? [];
  const infrastructureCostModels = overview?.infrastructure_cost_models ?? [];
  const infrastructureCostModelComponents = overview?.infrastructure_cost_model_components ?? [];
  const usageMetricDefinitions = overview?.infrastructure_usage_metric_definitions ?? [];
  const usageSources = overview?.infrastructure_usage_sources ?? [];
  const usageSeries = overview?.infrastructure_usage_series ?? [];
  const usageSamples = overview?.infrastructure_usage_samples ?? [];
  const usageRollups = overview?.infrastructure_usage_rollups ?? [];
  const usageRetentionPolicies = overview?.infrastructure_usage_retention_policies ?? [];
  const usageAttributionRules = overview?.infrastructure_usage_attribution_rules ?? [];
  const capacityDimensions = overview?.infrastructure_capacity_dimension_definitions ?? [];
  const capacitySources = overview?.infrastructure_capacity_sources ?? [];
  const capacityObservations = overview?.infrastructure_capacity_observations ?? [];
  const capacitySnapshot = overview?.infrastructure_capacity_utilization ?? {};
  const capacityRows = capacitySnapshot.rows ?? [];
  const telemetryRegistry = overview?.infrastructure_telemetry_registry ?? [];
  const telemetryBindings = overview?.infrastructure_telemetry_source_bindings ?? [];
  const telemetryVerifications = overview?.infrastructure_telemetry_verifications ?? [];
  const telemetrySnapshot = overview?.infrastructure_telemetry_snapshot ?? {};
  const telemetryRows = telemetrySnapshot.rows ?? [];
  const executionGate = overview?.execution_gate ?? {};
  const executionGateRows = executionGate.rows ?? [];
  const providerTruthContracts = overview?.provider_truth_contracts ?? [];
  const providerTruthRuns = overview?.provider_truth_collection_runs ?? [];
  const providerTruthBindings = overview?.provider_resource_truth_bindings ?? [];
  const providerTruthSnapshot = overview?.provider_resource_truth ?? {};
  const providerTruthRows = providerTruthSnapshot.rows ?? [];
  const providerMeasurementContracts = overview?.provider_measurement_contracts ?? [];
  const providerMeasurementRuns = overview?.provider_measurement_collection_runs ?? [];
  const applications = overview?.applications ?? [];
  const resources = (overview?.resources ?? []).filter(
    (item) => !item.immutable && item.authority_class !== "ENTERPRISE_ROOT" && item.lifecycle_state === "ACTIVE",
  );

  function parseDesigner() {
    return {
      requirements: JSON.parse(requirements || "[]"),
      provider_preferences: JSON.parse(providerPreferences || "{}"),
      resource_preferences: JSON.parse(resourcePreferences || "{}"),
      policy_constraints: JSON.parse(policyConstraints || "{}"),
    };
  }

  function parseOptimizer() {
    return {
      requirements: JSON.parse(requirements || "[]"),
      workload_profile: JSON.parse(workloadProfile || "{}"),
      objective_weights: JSON.parse(objectiveWeights || "{}"),
      hard_constraints: JSON.parse(optimizationConstraints || "{}"),
    };
  }

  async function previewRecommendations() {
    if (!applicationEntityKey) return;
    try {
      const parsed = parseOptimizer();
      const data = await invoke("enterprise_infrastructure_recommendation_preview", {
        application_entity_key: applicationEntityKey,
        environment_key: environmentKey,
        ...parsed,
        max_candidates: 5,
      }, "recommendation_preview");
      const result = data?.infrastructure_recommendation as RecommendationPreview | undefined;
      if (result) {
        setRecommendation(result);
        const missing = (result.missing_criteria ?? []).join(", ");
        setNotice(
          result.evidence_quality === "LOW"
            ? `Optimizer is READY, but evidence quality is LOW${missing ? ` because ${missing} are not yet sufficiently evidenced.` : "."} No infrastructure was changed.`
            : `Optimizer is ${result.readiness} with ${result.evidence_quality} evidence quality. No infrastructure was changed.`
        );
      }
    } catch {
      setNotice("Optimizer requirements, workload, weights and constraints must be valid JSON.");
    }
  }

  async function saveRecommendationSnapshot() {
    if (!applicationEntityKey) return;
    try {
      const parsed = parseOptimizer();
      const data = await invoke("enterprise_infrastructure_recommendation_snapshot", {
        application_entity_key: applicationEntityKey,
        environment_key: environmentKey,
        ...parsed,
        max_candidates: 5,
      }, "recommendation_snapshot");
      if (data?.infrastructure_recommendation_snapshot?.recommendation_id) {
        setNotice("Immutable advisory recommendation snapshot saved. No provider or application resource was changed.");
      }
    } catch {
      setNotice("Optimizer requirements, workload, weights and constraints must be valid JSON.");
    }
  }

  function useRecommendationsInDesigner() {
    if (!recommendation) return;
    setProviderPreferences(JSON.stringify(
      recommendation.recommended_overrides?.provider_preferences ?? {},
      null,
      2,
    ));
    setResourcePreferences(JSON.stringify(
      recommendation.recommended_overrides?.resource_preferences ?? {},
      null,
      2,
    ));
    setPreview(null);
    setNotice("Recommended overrides were copied into the Composition Designer only. Review them, then publish explicitly if you choose.");
  }

  async function previewComposition() {
    if (!applicationEntityKey) return;
    try {
      const parsed = parseDesigner();
      const data = await invoke("enterprise_composition_preview", {
        application_entity_key: applicationEntityKey,
        environment_key: environmentKey,
        ...parsed,
      }, "composition_preview");
      const result = data?.composition_preview as CompositionPreview | undefined;
      if (result) {
        setPreview(result);
        setNotice(result.readiness === "READY"
          ? "Composition is READY. Publish it to create an immutable infrastructure revision."
          : "Composition is BLOCKED. Resolve required slots or provider/resource overrides.");
      }
    } catch {
      setNotice("Composition requirements and preference fields must be valid JSON.");
    }
  }

  async function publishComposition() {
    if (!applicationEntityKey) return;
    try {
      const parsed = parseDesigner();
      const data = await invoke("enterprise_composition_publish", {
        application_entity_key: applicationEntityKey,
        environment_key: environmentKey,
        ...parsed,
        approval_id: compositionApprovalId.trim() || null,
      }, "composition_publish");
      if (data?.composition?.composition_id) {
        setNotice("Infrastructure composition revision published. It can now be materialized into Bridge / Flow.");
        setCompositionApprovalId("");
      }
    } catch {
      setNotice("Composition requirements and preference fields must be valid JSON.");
    }
  }

  async function materializeComposition(composition: Composition) {
    const data = await invoke("enterprise_composition_materialize", {
      composition_id: composition.composition_id,
      approval_id: materializeApprovalId.trim() || null,
    }, "composition_materialize");
    if (data?.composition_materialization?.materialized) {
      setNotice("Composition materialized into governed application assignments. DESIRED resources are now available in Flow.");
      setMaterializeApprovalId("");
    }
  }

  async function collectOwnerRootProviderTruth() {
    const data = await invoke(
      "enterprise_provider_truth_collect_owner_root",
      {},
      "provider_truth_owner_root",
    );
    const result = data?.provider_truth_collection as {
      run?: { status?: string; discovered_count?: number; verified_count?: number; unknown_count?: number };
      owner_root_mutated?: boolean;
      provider_mutation_performed?: boolean;
      unknown_values_fabricated?: boolean;
    } | undefined;
    if (result) {
      setNotice(
        `Owner Root truth: ${result.run?.status ?? "UNKNOWN"} · discovered ${result.run?.discovered_count ?? 0} · verified ${result.run?.verified_count ?? 0} · unknown ${result.run?.unknown_count ?? 0}. Provider mutation = ${result.provider_mutation_performed === true ? "YES" : "NO"}.`
      );
    }
  }

  async function collectCloudflareProviderTruth() {
    const data = await invoke(
      "enterprise_provider_truth_collect",
      { provider_key: "CLOUDFLARE" },
      "provider_truth_cloudflare",
    );
    const result = data?.provider_truth_collection as {
      run?: { status?: string; discovered_count?: number; verified_count?: number; unknown_count?: number };
      error_count?: number;
      provider_mutation_performed?: boolean;
    } | undefined;
    if (result) {
      setNotice(
        `Cloudflare truth: ${result.run?.status ?? "UNKNOWN"} · discovered ${result.run?.discovered_count ?? 0} · verified ${result.run?.verified_count ?? 0} · endpoint errors ${result.error_count ?? 0}. Provider mutation = ${result.provider_mutation_performed === true ? "YES" : "NO"}.`
      );
    }
  }

  async function collectMemberSupabaseProviderTruth() {
    const member = (overview?.resources ?? []).find(
      (item) =>
        item.provider_key === "SUPABASE"
        && item.resource_type === "PROJECT"
        && !!item.tenant_project_ref
        && item.resource_key !== "ROOT:OWNER_SUPABASE",
    );
    // Do not make the mobile action depend on the large infrastructure overview.
    // If overview is unavailable/stale, the Control Tower resolves the single
    // eligible verified member project server-side. Multiple projects still
    // require an explicit target instead of guessing.
    const payload: Record<string, unknown> = { provider_key: "SUPABASE" };
    if (member?.tenant_project_ref) {
      payload.tenant_project_ref = member.tenant_project_ref;
    }
    const data = await invoke(
      "enterprise_provider_truth_collect",
      payload,
      "provider_truth_supabase",
    );
    const result = data?.provider_truth_collection as {
      run?: { status?: string; discovered_count?: number; verified_count?: number; unknown_count?: number };
      optional_scope_gaps?: string[];
      error_count?: number;
      provider_mutation_performed?: boolean;
    } | undefined;
    if (result) {
      const gaps = result.optional_scope_gaps?.length ? ` · optional scope gaps: ${result.optional_scope_gaps.join(", ")}` : "";
      setNotice(
        `Supabase truth: ${result.run?.status ?? "UNKNOWN"} · discovered ${result.run?.discovered_count ?? 0} · verified ${result.run?.verified_count ?? 0} · endpoint errors ${result.error_count ?? 0}${gaps}. Provider mutation = ${result.provider_mutation_performed === true ? "YES" : "NO"}.`
      );
    }
  }

  async function collectCloudflareProviderMeasurements() {
    const data = await invoke(
      "enterprise_provider_measurement_collect",
      { provider_key: "CLOUDFLARE" },
      "provider_measurement_cloudflare",
    );
    const result = data?.provider_measurement_collection as {
      run?: {
        status?: string;
        usage_sample_count?: number;
        capacity_observation_count?: number;
        unknown_count?: number;
      };
      usage?: { accepted_count?: number };
      capacity?: { activated?: boolean; state?: string; reason?: string };
      errors?: string[];
      provider_mutation_performed?: boolean;
    } | undefined;
    if (result) {
      const accepted = result.run?.usage_sample_count ?? result.usage?.accepted_count ?? 0;
      setNotice(
        "Cloudflare measurements: " + (result.run?.status ?? "UNKNOWN") +
        " · usage samples " + accepted +
        " · capacity " + (result.capacity?.activated === true ? "ACTIVE" : (result.capacity?.state ?? "UNKNOWN")) +
        " · provider mutation = " + (result.provider_mutation_performed === true ? "YES" : "NO") + "."
      );
    }
  }

  async function collectSupabaseProviderMeasurements() {
    const data = await invoke(
      "enterprise_provider_measurement_collect",
      { provider_key: "SUPABASE" },
      "provider_measurement_supabase",
    );
    const result = data?.provider_measurement_collection as {
      run?: {
        status?: string;
        usage_sample_count?: number;
        capacity_observation_count?: number;
        telemetry_binding_count?: number;
        unknown_count?: number;
      };
      usage_sample_count?: number;
      capacity_observation_count?: number;
      telemetry_binding_count?: number;
      storage_capacity?: string;
      provider_mutation_performed?: boolean;
    } | undefined;
    if (result) {
      setNotice(
        "Supabase measurements: " + (result.run?.status ?? "UNKNOWN") +
        " · usage samples " + (result.run?.usage_sample_count ?? result.usage_sample_count ?? 0) +
        " · capacity observations " + (result.run?.capacity_observation_count ?? result.capacity_observation_count ?? 0) +
        " · telemetry bindings " + (result.run?.telemetry_binding_count ?? result.telemetry_binding_count ?? 0) +
        " · storage capacity " + (result.storage_capacity ?? "UNKNOWN") +
        " · provider mutation = " + (result.provider_mutation_performed === true ? "YES" : "NO") + "."
      );
    }
  }

  async function syncTelemetryResources() {
    const data = await invoke(
      "enterprise_telemetry_sync_resources",
      {},
      "telemetry_sync_resources",
    );
    const result = data?.infrastructure_telemetry_sync as {
      synchronized_resources?: number;
      owner_root_mutated?: boolean;
      provider_mutation_performed?: boolean;
    } | undefined;
    if (result) {
      setNotice(
        `Telemetry registry synchronized: ${result.synchronized_resources ?? 0} registered resources. Provider mutation = ${result.provider_mutation_performed === true ? "YES" : "NO"}.`
      );
    }
  }

  async function collectOwnerRootTelemetry() {
    const data = await invoke(
      "enterprise_telemetry_collect_owner_root",
      {},
      "telemetry_collect_owner_root",
    );
    const result = data?.infrastructure_telemetry_collection as {
      telemetry_key?: string;
      owner_root_mutated?: boolean;
      provider_mutation_performed?: boolean;
      unknown_values_fabricated?: boolean;
    } | undefined;
    if (result) {
      setNotice(
        `Owner Root telemetry refreshed for ${result.telemetry_key ?? "Owner Root"}. Root mutation = ${result.owner_root_mutated === true ? "YES" : "NO"}; fabricated unknowns = ${result.unknown_values_fabricated === true ? "YES" : "NO"}.`
      );
    }
  }

  async function collectOwnerRootCapacity() {
    const data = await invoke(
      "enterprise_capacity_collect_owner_root",
      {},
      "capacity_collect_owner_root",
    );
    const result = data?.infrastructure_capacity_collection as {
      accepted?: boolean;
      dimension_key?: string;
      capacity_value?: number;
      owner_root_mutated?: boolean;
    } | undefined;
    if (result) {
      setNotice(
        `Owner Root capacity refreshed: ${result.dimension_key ?? "capacity"} = ${result.capacity_value ?? "UNKNOWN"}. Owner Root mutation = ${result.owner_root_mutated === true ? "YES" : "NO"}.`
      );
    }
  }

  async function collectOwnerRootUsageStatistics() {
    const data = await invoke(
      "enterprise_usage_statistics_collect_owner_root",
      {},
      "usage_collect_owner_root",
    );
    const result = data?.infrastructure_usage_collection as {
      accepted_count?: number;
      rejected_count?: number;
      owner_root_observed?: boolean;
      owner_root_mutated?: boolean;
    } | undefined;
    if (result) {
      setNotice(
        `Owner Root usage snapshot collected: ${result.accepted_count ?? 0} samples accepted, ${result.rejected_count ?? 0} rejected. Observation only; Owner Root mutation = ${result.owner_root_mutated === true ? "YES" : "NO"}.`
      );
    }
  }

  async function buildHourlyUsageRollups() {
    const data = await invoke(
      "enterprise_usage_statistics_rollup",
      {
        resolution: "HOUR",
        window_start: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
        window_end: new Date().toISOString(),
      },
      "usage_rollup_hour",
    );
    const result = data?.infrastructure_usage_rollup as {
      rollup_rows?: number;
      resolution?: string;
    } | undefined;
    if (result) {
      setNotice(`Usage rollup complete: ${result.rollup_rows ?? 0} ${result.resolution ?? "HOUR"} statistics rows refreshed. No provider mutation occurred.`);
    }
  }

  async function ingestInternalEvidence() {
    const data = await invoke(
      "enterprise_infrastructure_evidence_ingest_internal",
      {},
      "evidence_ingest",
    );
    const result = data?.infrastructure_evidence_ingestion as {
      accepted_count?: number;
      promoted_count?: number;
      discovered_count?: number;
    } | undefined;
    if (result) {
      setNotice(
        `Evidence ingestion completed: ${result.accepted_count ?? 0} new observations accepted, ${result.promoted_count ?? 0} promoted to optimizer evidence. External PREPARABLE sources were not treated as connected.`
      );
    }
  }

  async function ingestExternalCatalog(sourceKey: string) {
    const data = await invoke(
      "enterprise_external_catalog_ingest",
      { source_keys: [sourceKey] },
      "external_catalog_ingest",
    );
    const result = data?.external_catalog_ingestion as {
      accepted_count?: number;
      discovered_count?: number;
      promoted_count?: number;
    } | undefined;
    if (result) {
      setNotice(
        `External catalog ingestion completed: ${result.accepted_count ?? 0} raw verified observations accepted. No universal COST score was generated.`
      );
    }
  }

  async function activateExternalEvidence(sourceKey: string) {
    if (!externalEvidenceApprovalId.trim()) {
      setNotice("A bound Owner activation approval UUID is required before probing and activating an external evidence source.");
      return;
    }
    const data = await invoke(
      "enterprise_external_evidence_activation_probe",
      {
        source_key: sourceKey,
        approval_id: externalEvidenceApprovalId.trim(),
      },
      "external_evidence_activation",
    );
    const activation = data?.activation as {
      verified?: boolean;
      source_state?: string;
      health_state?: string;
    } | undefined;
    if (activation) {
      setNotice(
        activation.verified
          ? `${sourceKey} probe VERIFIED. Source is now ${activation.source_state ?? "ACTIVE"}.`
          : `${sourceKey} did not verify and remains PREPARABLE. No provider mutation occurred.`
      );
      setExternalEvidenceApprovalId("");
    }
  }

  async function registerPool() {
    if (!poolKey.trim() || !poolName.trim() || !poolResourceType.trim()) return;
    try {
      const capacity = JSON.parse(poolCapacityPolicy || "{}");
      const data = await invoke("enterprise_resource_pool_register", {
        pool_key: poolKey.trim().toUpperCase().replace(/[^A-Z0-9_:\.-]/g, "_"),
        display_name: poolName.trim(),
        resource_type: poolResourceType.trim().toUpperCase(),
        environment_key: poolEnvironment === "ANY" ? null : poolEnvironment,
        provider_key: poolProvider.trim().toUpperCase() || null,
        allocation_policy: poolAllocationPolicy,
        capacity_policy: capacity,
        approval_id: poolApprovalId.trim() || null,
      }, "pool_register");
      if (data?.resource_pool?.pool_id) {
        setPoolKey("");
        setPoolName("");
        setPoolApprovalId("");
        setNotice("Enterprise resource pool registered.");
      }
    } catch {
      setNotice("Resource-pool capacity policy must be valid JSON.");
    }
  }

  async function addPoolMember() {
    if (!memberPoolKey || !memberResourceKey) return;
    const data = await invoke("enterprise_resource_pool_add_member", {
      pool_key: memberPoolKey,
      resource_key: memberResourceKey,
      capacity_weight: 1,
      approval_id: memberApprovalId.trim() || null,
    }, "pool_member");
    if (data?.pool_member?.resource_key) {
      setMemberResourceKey("");
      setMemberApprovalId("");
      setNotice("Resource added to enterprise pool.");
    }
  }

  if (!connected) return null;

  if (mode === "applications") {
    return (
      <Card className="border-foreground/8 bg-card py-0">
        <CardContent className="p-4">
          <Header
            icon={AppWindow}
            title="Infrastructure compositions"
            description="Immutable infrastructure revisions per application and environment, including Factory-generated compositions."
          />
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-5">
            <Metric label="Compositions" value={overviewMetric(compositions.length)} />
            <Metric label="Ready" value={overviewMetric(compositions.filter((item) => item.readiness === "READY").length)} />
            <Metric label="Active" value={overviewMetric(compositions.filter((item) => item.state === "ACTIVE").length)} />
            <Metric label="Blocked" value={overviewMetric(compositions.filter((item) => item.readiness === "BLOCKED").length)} />
            <Metric label="Optimizer snapshots" value={overviewMetric(recommendationSnapshots.length)} />
          </div>
          <div className="mt-3 space-y-2">
            {overviewReady && compositions.length === 0 && <Empty text="No infrastructure compositions have been published yet." />}
            {compositions.map((composition) => {
              const app = applications.find((item) => item.entity_key === composition.application_entity_key);
              const compSlots = slots.filter((item) => item.composition_id === composition.composition_id);
              return (
                <div key={composition.composition_id} className="rounded-xl border border-foreground/8 bg-foreground/[0.02] p-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="text-xs font-semibold">{app?.display_name ?? composition.application_entity_key}</p>
                      <p className="mt-1 font-mono text-[9px] text-foreground/35">{composition.composition_key}</p>
                    </div>
                    <div className="flex gap-1.5">
                      <Badge className={tone(composition.state)}>{composition.state}</Badge>
                      <Badge className={tone(composition.readiness)}>{composition.environment_key} · {composition.readiness}</Badge>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {compSlots.map((slot) => (
                      <Badge key={slot.slot_id} className={tone(slot.state)}>
                        {slot.slot_key.replaceAll("_", " ")} · {slot.state}
                      </Badge>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {recommendationSnapshots.length > 0 && (
            <div className="mt-4 rounded-xl bg-foreground/[0.025] p-3">
              <p className="text-xs font-semibold">Latest infrastructure recommendations</p>
              <div className="mt-2 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {recommendationSnapshots.slice(0, 6).map((item) => {
                  const app = applications.find((candidate) => candidate.entity_key === item.application_entity_key);
                  return (
                    <div key={item.recommendation_id} className="rounded-lg bg-background/60 p-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <p className="truncate text-[10px] font-semibold">{app?.display_name ?? item.application_entity_key}</p>
                        <Badge className={tone(item.state)}>{item.evidence_quality}</Badge>
                      </div>
                      <p className="mt-1 text-[9px] text-foreground/38">
                        {item.environment_key} · r{item.revision} · {Number(item.evidence_coverage_pct).toFixed(0)}% evidence
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {notice && <Notice text={notice} />}
        </CardContent>
      </Card>
    );
  }

  if (mode === "infrastructure") {
    return (
      <Card className="border-foreground/8 bg-card py-0">
        <CardContent className="p-4">
          <Header
            icon={HardDrive}
            title="Enterprise infrastructure truth, telemetry & evidence"
            description="See authoritative RUNYI resources alongside read-only provider truth, verification freshness, capacity/utilization, usage and evidence from one Control Tower."
          />

          <div className="mt-3 grid grid-cols-3 gap-1.5 sm:grid-cols-6">
            <Metric label="Resource pools" value={projectedMetric("resource_pools")} />
            <Metric label="Pool members" value={projectedMetric("pool_members")} />
            <Metric label="Evidence sources" value={projectedMetric("evidence_sources")} />
            <Metric label="Active feeds" value={projectedMetric("active_feeds")} />
            <Metric label="Preparable feeds" value={projectedMetric("preparable_feeds")} />
            <Metric label="Verified activations" value={projectedMetric("verified_activations")} />
            <Metric label="Catalog entries" value={projectedMetric("catalog_entries")} />
            <Metric label="Cost models" value={projectedMetric("cost_models")} />
            <Metric label="Usage metrics" value={projectedMetric("usage_metrics")} />
            <Metric label="Usage series" value={projectedMetric("usage_series")} />
            <Metric label="Telemetry targets" value={projectedMetric("telemetry_targets")} />
            <Metric label="Source bindings" value={projectedMetric("source_bindings")} />
          </div>

          <div className="mt-3 rounded-xl border border-foreground/8 bg-foreground/[0.02] p-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-2.5">
                <div className="grid size-8 shrink-0 place-items-center rounded-xl bg-primary/[0.08] text-primary">
                  <Activity className="size-4" />
                </div>
                <div>
                  <p className="text-xs font-semibold">Universal Provider Resource Truth Plane</p>
                  <p className="mt-1 text-[10px] leading-5 text-foreground/45">
                    Read-only provider discovery shows what actually exists now and preserves provenance. RUNYI enterprise resources remain canonical identity; usage stays under v1.17, capacity under v1.18, telemetry under v1.19 and mutation decisions under v1.20.
                  </p>
                </div>
              </div>
              <div className="grid w-full gap-2 sm:w-auto sm:grid-cols-3">
                <Button
                  type="button"
                  variant="outline"
                  className="relative z-10 min-h-12 w-full touch-manipulation pointer-events-auto sm:min-h-9"
                  onClick={() => void collectOwnerRootProviderTruth()}
                  disabled={busy === "provider_truth_owner_root"}
                >
                  {busy === "provider_truth_owner_root" ? <LoaderCircle className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
                  Owner Root truth
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="relative z-10 min-h-12 w-full touch-manipulation pointer-events-auto sm:min-h-9"
                  onClick={() => void collectCloudflareProviderTruth()}
                  disabled={busy === "provider_truth_cloudflare"}
                >
                  {busy === "provider_truth_cloudflare" ? <LoaderCircle className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
                  Cloudflare truth
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="relative z-10 min-h-12 w-full touch-manipulation pointer-events-auto sm:min-h-9"
                  onClick={() => void collectMemberSupabaseProviderTruth()}
                  disabled={busy === "provider_truth_supabase"}
                >
                  {busy === "provider_truth_supabase" ? <LoaderCircle className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
                  Supabase truth
                </Button>
              </div>
            </div>

            <div className="mt-3 grid grid-cols-3 gap-1.5 sm:grid-cols-6">
              <Metric label="Contracts" value={overviewMetric(providerTruthContracts.length)} />
              <Metric label="Active" value={overviewMetric(providerTruthContracts.filter((item) => item.lifecycle_state === "ACTIVE").length)} />
              <Metric label="Preparable" value={overviewMetric(providerTruthContracts.filter((item) => item.lifecycle_state === "PREPARABLE").length)} />
              <Metric label="Truth rows" value={overviewMetric(Number(providerTruthSnapshot.row_count ?? providerTruthRows.length))} />
              <Metric label="Fresh" value={overviewMetric(providerTruthRows.filter((item) => item.freshness_state === "FRESH").length)} />
              <Metric label="Bound" value={overviewMetric(providerTruthBindings.filter((item) => item.binding_state === "VERIFIED").length)} />
            </div>

            <div className="mt-3 space-y-2 sm:hidden">
              {overviewReady && providerTruthRows.length === 0 && <Empty text="No live provider truth observations yet. Collect a provider to activate verified read-only state." />}
              {providerTruthRows.slice(0, 75).map((row) => (
                <div key={row.observation_id} className="rounded-xl border border-foreground/8 bg-background/60 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-[11px] font-semibold">{row.provider_resource_name ?? row.provider_resource_id}</p>
                      <p className="mt-0.5 truncate text-[8px] text-foreground/45">{row.provider_key} · {row.provider_resource_type}</p>
                    </div>
                    <Badge className={tone(row.health_state)}>{row.health_state}</Badge>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <MobileFact label="Verification"><Badge className={tone(row.verification_state)}>{row.verification_state}</Badge></MobileFact>
                    <MobileFact label="Freshness"><Badge className={tone(row.freshness_state)}>{row.freshness_state}</Badge></MobileFact>
                    <MobileFact label="RUNYI binding" wide>
                      <span className="block truncate text-[9px] font-medium">{row.resource_key ?? "UNBOUND"}</span>
                      <span className="block truncate text-[8px] text-foreground/42">{row.telemetry_key ?? "No telemetry binding"}</span>
                    </MobileFact>
                    <MobileFact label="Plan / tier">
                      <span className="text-[9px]">{row.plan_ref ?? "UNKNOWN"} · {row.tier_ref ?? "UNKNOWN"}</span>
                    </MobileFact>
                    <MobileFact label="Observed">
                      <span className="text-[9px]">{new Date(row.observed_at).toLocaleString()}</span>
                      <span className="mt-0.5 block text-[8px] text-foreground/42">{row.unknown_fields?.length ?? 0} unknown fields</span>
                    </MobileFact>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-3 hidden overflow-x-auto rounded-xl bg-background/60 sm:block">
              <div className="min-w-[1040px]">
                <div className="grid grid-cols-[minmax(230px,1.4fr)_110px_130px_120px_170px_110px_160px] gap-2 border-b border-foreground/8 px-3 py-2 text-[8px] font-semibold uppercase tracking-wider text-foreground/35">
                  <span>Provider resource</span>
                  <span>Health</span>
                  <span>Verification</span>
                  <span>Freshness</span>
                  <span>RUNYI binding</span>
                  <span>Plan / tier</span>
                  <span className="text-right">Observed</span>
                </div>
                {providerTruthRows.length === 0 && (
                  <div className="p-3"><Empty text="No live provider truth observations yet. Collect a provider to activate verified read-only state." /></div>
                )}
                {providerTruthRows.slice(0, 75).map((row) => (
                  <div
                    key={row.observation_id}
                    className="grid grid-cols-[minmax(230px,1.4fr)_110px_130px_120px_170px_110px_160px] items-center gap-2 border-b border-foreground/5 px-3 py-2.5 last:border-b-0"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-[10px] font-semibold">{row.provider_resource_name ?? row.provider_resource_id}</p>
                      <p className="mt-0.5 truncate text-[8px] text-foreground/32">{row.provider_key} · {row.provider_resource_type}</p>
                    </div>
                    <div><Badge className={tone(row.health_state)}>{row.health_state}</Badge></div>
                    <div><Badge className={tone(row.verification_state)}>{row.verification_state}</Badge></div>
                    <div><Badge className={tone(row.freshness_state)}>{row.freshness_state}</Badge></div>
                    <div className="min-w-0">
                      <p className="truncate text-[9px] font-medium">{row.resource_key ?? "UNBOUND"}</p>
                      <p className="mt-0.5 truncate text-[7px] text-foreground/30">{row.telemetry_key ?? "No telemetry binding"}</p>
                    </div>
                    <div>
                      <p className="text-[9px]">{row.plan_ref ?? "UNKNOWN"}</p>
                      <p className="mt-0.5 text-[7px] text-foreground/30">{row.tier_ref ?? "UNKNOWN"}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[9px]">{new Date(row.observed_at).toLocaleString()}</p>
                      <p className="mt-0.5 text-[7px] text-foreground/30">{row.unknown_fields?.length ?? 0} unknown fields</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-3 rounded-lg bg-primary/[0.045] px-3 py-2 text-[9px] leading-5 text-foreground/50">
              Recent collection runs: {overviewMetric(providerTruthRuns.length)}. UNKNOWN is never interpreted as absent or zero. Provider truth is read-only and cannot mutate Owner Root or external provider resources.
            </div>
          </div>

          <div className="mt-3 rounded-xl border border-foreground/8 bg-foreground/[0.02] p-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-2.5">
                <div className="grid size-8 shrink-0 place-items-center rounded-xl bg-primary/[0.08] text-primary">
                  <Activity className="size-4" />
                </div>
                <div>
                  <p className="text-xs font-semibold">Universal Provider Usage & Capacity Activation Plane</p>
                  <p className="mt-1 text-[10px] leading-5 text-foreground/45">
                    v1.22 verifies exact provider measurement paths, then promotes measured usage only into v1.17 and verified limits only into v1.18. v1.19 composes those authorities into telemetry. Missing limits remain UNKNOWN; this plane never mutates provider resources.
                  </p>
                </div>
              </div>
              <div className="grid w-full gap-2 sm:w-auto sm:grid-cols-2">
                <Button
                  type="button"
                  variant="outline"
                  className="relative z-10 min-h-12 w-full touch-manipulation pointer-events-auto sm:min-h-9"
                  onClick={() => void collectCloudflareProviderMeasurements()}
                  disabled={busy === "provider_measurement_cloudflare"}
                >
                  {busy === "provider_measurement_cloudflare" ? <LoaderCircle className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
                  Cloudflare measurements
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="relative z-10 min-h-12 w-full touch-manipulation pointer-events-auto sm:min-h-9"
                  onClick={() => void collectSupabaseProviderMeasurements()}
                  disabled={busy === "provider_measurement_supabase"}
                >
                  {busy === "provider_measurement_supabase" ? <LoaderCircle className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
                  Supabase measurements
                </Button>
              </div>
            </div>

            <div className="mt-3 grid grid-cols-3 gap-1.5 sm:grid-cols-6">
              <Metric label="Contracts" value={overviewMetric(providerMeasurementContracts.length)} />
              <Metric label="Active" value={overviewMetric(providerMeasurementContracts.filter((item) => item.lifecycle_state === "ACTIVE").length)} />
              <Metric label="Preparable" value={overviewMetric(providerMeasurementContracts.filter((item) => item.lifecycle_state === "PREPARABLE").length)} />
              <Metric label="Catalogued" value={overviewMetric(providerMeasurementContracts.filter((item) => item.lifecycle_state === "CATALOGUED").length)} />
              <Metric label="Runs" value={overviewMetric(providerMeasurementRuns.length)} />
              <Metric label="Succeeded" value={overviewMetric(providerMeasurementRuns.filter((item) => item.status === "SUCCEEDED").length)} />
            </div>

            <div className="mt-3 space-y-2 sm:hidden">
              {overviewReady && providerMeasurementContracts.length === 0 && <Empty text="No provider measurement activation contracts are registered yet." />}
              {providerMeasurementContracts.map((item) => {
                const usage = usageSources.find((source) => source.source_key === item.usage_source_key);
                const capacity = capacitySources.find((source) => source.source_key === item.capacity_source_key);
                return (
                  <div key={item.contract_key} className="rounded-xl border border-foreground/8 bg-background/60 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-[11px] font-semibold">{item.display_name}</p>
                        <p className="mt-0.5 truncate text-[8px] text-foreground/45">{item.provider_key} · {item.contract_key}</p>
                      </div>
                      <Badge className={tone(item.lifecycle_state)}>{item.lifecycle_state}</Badge>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <MobileFact label="Usage authority">
                        <span className="block truncate text-[8px]">{item.usage_source_key ?? "NONE"}</span>
                        <Badge className={tone(usage?.lifecycle_state ?? "UNKNOWN")}>{usage?.lifecycle_state ?? "UNKNOWN"}</Badge>
                      </MobileFact>
                      <MobileFact label="Capacity authority">
                        <span className="block truncate text-[8px]">{item.capacity_source_key ?? "NONE"}</span>
                        <Badge className={tone(capacity?.lifecycle_state ?? "UNKNOWN")}>{capacity?.lifecycle_state ?? "UNKNOWN"}</Badge>
                      </MobileFact>
                      <MobileFact label="Health"><Badge className={tone(item.health_state)}>{item.health_state}</Badge></MobileFact>
                      <MobileFact label="Last success"><span className="text-[9px]">{item.last_success_at ? new Date(item.last_success_at).toLocaleString() : "UNKNOWN"}</span></MobileFact>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-3 hidden overflow-x-auto rounded-xl bg-background/60 sm:block">
              <div className="min-w-[980px]">
                <div className="grid grid-cols-[minmax(250px,1.5fr)_120px_150px_150px_100px_170px] gap-2 border-b border-foreground/8 px-3 py-2 text-[8px] font-semibold uppercase tracking-wider text-foreground/35">
                  <span>Activation contract</span>
                  <span>State</span>
                  <span>Usage authority</span>
                  <span>Capacity authority</span>
                  <span>Health</span>
                  <span className="text-right">Last success</span>
                </div>
                {providerMeasurementContracts.length === 0 && (
                  <div className="p-3"><Empty text="No provider measurement activation contracts are registered yet." /></div>
                )}
                {providerMeasurementContracts.map((item) => {
                  const usage = usageSources.find((source) => source.source_key === item.usage_source_key);
                  const capacity = capacitySources.find((source) => source.source_key === item.capacity_source_key);
                  return (
                    <div
                      key={item.contract_key}
                      className="grid grid-cols-[minmax(250px,1.5fr)_120px_150px_150px_100px_170px] items-center gap-2 border-b border-foreground/5 px-3 py-2.5 last:border-b-0"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-[10px] font-semibold">{item.display_name}</p>
                        <p className="mt-0.5 truncate text-[8px] text-foreground/32">{item.provider_key} · {item.contract_key}</p>
                      </div>
                      <div><Badge className={tone(item.lifecycle_state)}>{item.lifecycle_state}</Badge></div>
                      <div>
                        <p className="truncate text-[9px]">{item.usage_source_key ?? "NONE"}</p>
                        <Badge className={tone(usage?.lifecycle_state ?? "UNKNOWN")}>{usage?.lifecycle_state ?? "UNKNOWN"}</Badge>
                      </div>
                      <div>
                        <p className="truncate text-[9px]">{item.capacity_source_key ?? "NONE"}</p>
                        <Badge className={tone(capacity?.lifecycle_state ?? "UNKNOWN")}>{capacity?.lifecycle_state ?? "UNKNOWN"}</Badge>
                      </div>
                      <div><Badge className={tone(item.health_state)}>{item.health_state}</Badge></div>
                      <div className="text-right text-[9px]">
                        {item.last_success_at ? new Date(item.last_success_at).toLocaleString() : "UNKNOWN"}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-3 rounded-lg bg-primary/[0.045] px-3 py-2 text-[9px] leading-5 text-foreground/50">
              Authorities remain fixed: usage = v1.17 · capacity = v1.18 · telemetry = v1.19 · mutation gate = v1.20 · provider identity/provenance = v1.21. v1.22 activates verified read-only measurement paths only.
            </div>
          </div>

          <div className="mt-3 rounded-xl border border-foreground/8 bg-foreground/[0.02] p-3">
            <div className="flex min-w-0 items-start gap-2.5">
              <div className="grid size-8 shrink-0 place-items-center rounded-xl bg-primary/[0.08] text-primary">
                <ShieldCheck className="size-4" />
              </div>
              <div>
                <p className="text-xs font-semibold">Universal Idempotent Execution Gate</p>
                <p className="mt-1 text-[10px] leading-5 text-foreground/45">
                  Every provider mutation must discover authoritative current state, compare it with desired state, and receive a gate decision before execution. Already-satisfied steps are skipped; unknown or conflicting state is blocked for verification or reconciliation.
                </p>
              </div>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Metric label="Evaluations" value={overviewMetric(Number(executionGate.row_count ?? executionGateRows.length))} />
              <Metric label="Already satisfied" value={overviewMetric(Number(executionGate.already_satisfied ?? 0))} />
              <Metric label="Active locks" value={overviewMetric(Number(executionGate.active_locks ?? 0))} />
              <Metric label="Blocked / unverified" value={overviewMetric(Number(executionGate.blocked_or_unverified ?? 0))} />
            </div>

            <div className="mt-3 space-y-2 sm:hidden">
              {overviewReady && executionGateRows.length === 0 && <Empty text="No execution-gate evaluations yet." />}
              {executionGateRows.slice(0, 50).map((row) => (
                <div key={row.gate_id} className="rounded-xl border border-foreground/8 bg-background/60 p-3">
                  <div className="min-w-0">
                    <p className="truncate text-[11px] font-semibold">{row.target_key}</p>
                    <p className="mt-0.5 truncate text-[8px] text-foreground/45">{row.action_key} · {row.authority_key}</p>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <MobileFact label="Presence"><Badge className={tone(row.presence_state)}>{row.presence_state}</Badge></MobileFact>
                    <MobileFact label="Decision"><Badge className={tone(row.decision)}>{row.decision.replaceAll("_", " ")}</Badge></MobileFact>
                    <MobileFact label="Directive"><Badge className={tone(row.execution_directive)}>{row.execution_directive.replaceAll("_", " ")}</Badge></MobileFact>
                    <MobileFact label="Lock"><Badge className={tone(row.lock?.status ?? "NONE")}>{row.lock?.status ?? "NONE"}</Badge></MobileFact>
                    <MobileFact label="Discovered" wide>
                      <span className="text-[9px]">{row.discovered_at ? new Date(row.discovered_at).toLocaleString() : "UNKNOWN"}</span>
                      <span className="ml-2 text-[8px] text-foreground/45">{row.observed_verified ? "VERIFIED READBACK" : "UNVERIFIED"}</span>
                    </MobileFact>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-3 hidden overflow-x-auto rounded-xl bg-background/60 sm:block">
              <div className="min-w-[920px]">
                <div className="grid grid-cols-[minmax(220px,1.4fr)_140px_140px_140px_120px_150px] gap-2 border-b border-foreground/8 px-3 py-2 text-[8px] font-semibold uppercase tracking-wider text-foreground/35">
                  <span>Target / Action</span>
                  <span>Presence</span>
                  <span>Decision</span>
                  <span>Directive</span>
                  <span>Lock</span>
                  <span className="text-right">Discovered</span>
                </div>
                {executionGateRows.length === 0 && (
                  <div className="p-3"><Empty text="No execution-gate evaluations yet." /></div>
                )}
                {executionGateRows.slice(0, 50).map((row) => (
                  <div
                    key={row.gate_id}
                    className="grid grid-cols-[minmax(220px,1.4fr)_140px_140px_140px_120px_150px] items-center gap-2 border-b border-foreground/5 px-3 py-2.5 last:border-b-0"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-[10px] font-semibold">{row.target_key}</p>
                      <p className="mt-0.5 truncate text-[8px] text-foreground/32">{row.action_key} · {row.authority_key}</p>
                    </div>
                    <div><Badge className={tone(row.presence_state)}>{row.presence_state}</Badge></div>
                    <div><Badge className={tone(row.decision)}>{row.decision.replaceAll("_", " ")}</Badge></div>
                    <div><Badge className={tone(row.execution_directive)}>{row.execution_directive.replaceAll("_", " ")}</Badge></div>
                    <div>
                      <Badge className={tone(row.lock?.status ?? "NONE")}>{row.lock?.status ?? "NONE"}</Badge>
                    </div>
                    <div className="text-right">
                      <p className="text-[9px]">{row.discovered_at ? new Date(row.discovered_at).toLocaleString() : "UNKNOWN"}</p>
                      <p className="mt-0.5 text-[7px] text-foreground/30">{row.observed_verified ? "VERIFIED READBACK" : "UNVERIFIED"}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-3 rounded-lg bg-primary/[0.045] px-3 py-2 text-[9px] leading-5 text-foreground/50">
              Permanent rule: discover before create · compare before change · verify before trust · lock before mutate · apply only the minimum required delta · record every decision.
            </div>
          </div>

          <div className="mt-3 rounded-xl border border-foreground/8 bg-foreground/[0.02] p-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-2.5">
                <div className="grid size-8 shrink-0 place-items-center rounded-xl bg-primary/[0.08] text-primary">
                  <Activity className="size-4" />
                </div>
                <div>
                  <p className="text-xs font-semibold">Universal Infrastructure Telemetry Registry</p>
                  <p className="mt-1 text-[10px] leading-5 text-foreground/45">
                    One resource-centered view composes RUNYI resource identity, exact source bindings, verification freshness, application assignments and v1.18 capacity state. Missing plan, tier, cost or capacity remains UNKNOWN; telemetry does not manufacture provider facts.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={() => void syncTelemetryResources()}
                  disabled={busy === "telemetry_sync_resources"}
                >
                  {busy === "telemetry_sync_resources" ? <LoaderCircle className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
                  Sync resource identities
                </Button>
                <Button
                  variant="outline"
                  onClick={() => void collectOwnerRootTelemetry()}
                  disabled={busy === "telemetry_collect_owner_root"}
                >
                  {busy === "telemetry_collect_owner_root" ? <LoaderCircle className="size-4 animate-spin" /> : <Activity className="size-4" />}
                  Refresh Owner Root telemetry
                </Button>
              </div>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-8">
              <Metric label="Targets" value={overviewMetric(telemetryRows.length)} />
              <Metric label="Verified identity" value={overviewMetric(telemetryRows.filter((item) => item.identity_verification_state === "VERIFIED").length)} />
              <Metric label="Fresh" value={overviewMetric(telemetryRows.filter((item) => item.freshness_state === "FRESH").length)} />
              <Metric label="Stale" value={overviewMetric(telemetryRows.filter((item) => item.freshness_state === "STALE").length)} />
              <Metric label="Healthy" value={overviewMetric(telemetryRows.filter((item) => item.health_state === "HEALTHY").length)} />
              <Metric label="Warning" value={overviewMetric(telemetryRows.filter((item) => item.health_state === "WARNING").length)} />
              <Metric label="Critical" value={overviewMetric(telemetryRows.filter((item) => item.health_state === "CRITICAL").length)} />
              <Metric label="Exhausted" value={overviewMetric(telemetryRows.filter((item) => item.health_state === "EXHAUSTED").length)} />
            </div>

            <div className="mt-3 overflow-x-auto rounded-xl bg-background/60">
              <div className="min-w-[900px]">
                <div className="grid grid-cols-[minmax(210px,1.5fr)_110px_100px_160px_110px_170px] gap-2 border-b border-foreground/8 px-3 py-2 text-[8px] font-semibold uppercase tracking-wider text-foreground/35">
                  <span>Resource / Provider</span>
                  <span>Health</span>
                  <span>Freshness</span>
                  <span>Region / Plan</span>
                  <span className="text-right">Sources</span>
                  <span className="text-right">Last verified</span>
                </div>
                {telemetryRows.length === 0 && (
                  <div className="p-3"><Empty text="No telemetry targets are registered yet. Synchronize registered enterprise resources." /></div>
                )}
                {telemetryRows.slice(0, 50).map((row) => (
                  <div
                    key={row.telemetry_key}
                    className="grid grid-cols-[minmax(210px,1.5fr)_110px_100px_160px_110px_170px] items-center gap-2 border-b border-foreground/5 px-3 py-2.5 last:border-b-0"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-[10px] font-semibold">{row.resource_key ?? row.target_key}</p>
                      <p className="mt-0.5 truncate text-[8px] text-foreground/32">
                        {row.provider_key ?? "RUNYI"} · {row.resource_type ?? row.target_kind} · {row.environment_key ?? "UNKNOWN"}
                      </p>
                    </div>
                    <div><Badge className={tone(row.health_state)}>{row.health_state}</Badge></div>
                    <div><Badge className={tone(row.freshness_state)}>{row.freshness_state}</Badge></div>
                    <div className="min-w-0">
                      <p className="truncate text-[9px]">{row.region_ref ?? "UNKNOWN REGION"}</p>
                      <p className="mt-0.5 truncate text-[8px] text-foreground/32">
                        {row.plan_ref ?? "UNKNOWN PLAN"}{row.tier_ref ? ` · ${row.tier_ref}` : ""}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] font-semibold">{row.source_bindings?.length ?? 0}</p>
                      <p className="mt-0.5 text-[7px] text-foreground/30">{row.capacity?.length ?? 0} capacity rows</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[9px]">{row.last_verified_at ? new Date(row.last_verified_at).toLocaleString() : "UNKNOWN"}</p>
                      <p className="mt-0.5 text-[7px] text-foreground/30">{row.identity_verification_state.replaceAll("_", " ")}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-3 rounded-lg bg-primary/[0.045] px-3 py-2 text-[9px] leading-5 text-foreground/50">
              Telemetry verification records: {telemetryVerifications.length}. ESTIMATED is allowed only for governed COST projections. Usage and capacity stay measured/verified or UNKNOWN, and shared application usage is never inferred.
            </div>
          </div>

          <div className="mt-3 rounded-xl border border-foreground/8 bg-foreground/[0.02] p-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-2.5">
                <div className="grid size-8 shrink-0 place-items-center rounded-xl bg-primary/[0.08] text-primary">
                  <Database className="size-4" />
                </div>
                <div>
                  <p className="text-xs font-semibold">Universal Infrastructure Capacity & Utilization</p>
                  <p className="mt-1 text-[10px] leading-5 text-foreground/45">
                    Verified capacity facts are paired with compatible usage measurements to show Capacity, Used, Remaining and Utilization. Unknown provider limits remain UNKNOWN; RUNYI never infers a storage, compute, edge or quota ceiling from usage alone.
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => void collectOwnerRootCapacity()}
                disabled={busy === "capacity_collect_owner_root"}
              >
                {busy === "capacity_collect_owner_root" ? <LoaderCircle className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
                Refresh Owner Root capacity
              </Button>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-8">
              <Metric label="Capacity dimensions" value={overviewMetric(capacityDimensions.length)} />
              <Metric label="Capacity sources" value={overviewMetric(capacitySources.length)} />
              <Metric label="Active sources" value={overviewMetric(capacitySources.filter((item) => item.lifecycle_state === "ACTIVE").length)} />
              <Metric label="Observations" value={overviewMetric(capacityObservations.length)} />
              <Metric label="Healthy" value={overviewMetric(capacityRows.filter((item) => item.status === "HEALTHY").length)} />
              <Metric label="Warning" value={overviewMetric(capacityRows.filter((item) => item.status === "WARNING").length)} />
              <Metric label="Critical" value={overviewMetric(capacityRows.filter((item) => ["CRITICAL", "OVER_CAPACITY"].includes(item.status)).length)} />
              <Metric label="Unknown" value={overviewMetric(capacityRows.filter((item) => ["CAPACITY_UNKNOWN", "USAGE_UNKNOWN"].includes(item.status)).length)} />
            </div>

            <div className="mt-3 overflow-x-auto rounded-xl bg-background/60">
              <div className="min-w-[760px]">
                <div className="grid grid-cols-[minmax(180px,1.5fr)_100px_120px_120px_120px_110px] gap-2 border-b border-foreground/8 px-3 py-2 text-[8px] font-semibold uppercase tracking-wider text-foreground/35">
                  <span>Resource / Capacity</span>
                  <span>Status</span>
                  <span className="text-right">Capacity</span>
                  <span className="text-right">Used</span>
                  <span className="text-right">Remaining</span>
                  <span className="text-right">Utilization</span>
                </div>
                {capacityRows.length === 0 && (
                  <div className="p-3"><Empty text="No capacity/utilization rows are available yet. Connect or collect a verified capacity source." /></div>
                )}
                {capacityRows.slice(0, 40).map((row) => (
                  <div
                    key={row.row_key}
                    className="grid grid-cols-[minmax(180px,1.5fr)_100px_120px_120px_120px_110px] items-center gap-2 border-b border-foreground/5 px-3 py-2.5 last:border-b-0"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-[10px] font-semibold">{row.display_name}</p>
                      <p className="mt-0.5 truncate text-[8px] text-foreground/32">
                        {row.provider_key ?? "RUNYI"} · {row.target_key} · {row.resource_domain}
                      </p>
                    </div>
                    <div><Badge className={tone(row.status)}>{row.status.replaceAll("_", " ")}</Badge></div>
                    <p className="text-right text-[10px] font-semibold">{formatCapacityValue(row.capacity_value, row.canonical_unit)}</p>
                    <p className="text-right text-[10px]">{formatCapacityValue(row.used_value, row.canonical_unit)}</p>
                    <p className="text-right text-[10px]">{formatCapacityValue(row.remaining_value, row.canonical_unit)}</p>
                    <div className="text-right">
                      <p className="text-[10px] font-semibold">
                        {row.utilization_pct == null ? "UNKNOWN" : `${Number(row.utilization_pct).toFixed(1)}%`}
                      </p>
                      {row.headroom_pct != null && (
                        <p className="mt-0.5 text-[7px] text-foreground/30">{Number(row.headroom_pct).toFixed(1)}% headroom</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
              {capacitySources.map((source) => (
                <ResponsiveStatusRow key={source.source_key} className="bg-background/60"
                  title={source.display_name} detail={<span className="font-mono">{source.source_key}</span>}
                  category={`${source.provider_key ?? "RUNYI"} · ${source.source_class.replaceAll("_", " ")} · ${source.trust_level.replaceAll("_", " ")}`}
                  status={source.lifecycle_state} statusClassName={tone(source.lifecycle_state)} />
              ))}
            </div>

            <div className="mt-3 rounded-lg bg-primary/[0.045] px-3 py-2 text-[9px] leading-5 text-foreground/50">
              Current verified example: PostgreSQL connection capacity is read from the actual server configuration and paired with live connection usage. Database/storage/edge/compute capacity stays UNKNOWN until a verified provider limit or provisioned resource fact is connected.
            </div>
          </div>

          <div className="mt-3 rounded-xl border border-foreground/8 bg-foreground/[0.02] p-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-2.5">
                <div className="grid size-8 shrink-0 place-items-center rounded-xl bg-primary/[0.08] text-primary">
                  <Activity className="size-4" />
                </div>
                <div>
                  <p className="text-xs font-semibold">Unlimited Enterprise Universal Infrastructure Usage Statistics</p>
                  <p className="mt-1 text-[10px] leading-5 text-foreground/45">
                    Universal metric registry + verified sources + time-series samples + rollups + explicit attribution. Target kinds and dimensions are data-driven, so future databases, storage systems, runtimes, services, agents, workflows and applications do not require a new statistics schema. Owner Root is observable read-only and shared usage is never assigned to an application by guesswork.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={() => void collectOwnerRootUsageStatistics()}
                  disabled={busy === "usage_collect_owner_root"}
                >
                  {busy === "usage_collect_owner_root" ? <LoaderCircle className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
                  Collect Owner Root stats
                </Button>
                <Button
                  variant="outline"
                  onClick={() => void buildHourlyUsageRollups()}
                  disabled={busy === "usage_rollup_hour"}
                >
                  {busy === "usage_rollup_hour" ? <LoaderCircle className="size-4 animate-spin" /> : <Activity className="size-4" />}
                  Build 24h hourly rollups
                </Button>
              </div>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-8">
              <Metric label="Metric definitions" value={overviewMetric(usageMetricDefinitions.length)} />
              <Metric label="Statistic sources" value={overviewMetric(usageSources.length)} />
              <Metric label="Active sources" value={overviewMetric(usageSources.filter((item) => item.lifecycle_state === "ACTIVE").length)} />
              <Metric label="Live series" value={overviewMetric(usageSeries.length)} />
              <Metric label="Recent samples" value={overviewMetric(usageSamples.length)} />
              <Metric label="Recent rollups" value={overviewMetric(usageRollups.length)} />
              <Metric label="Attributed series" value={overviewMetric(usageSeries.filter((item) => item.application_entity_key != null).length)} />
              <Metric label="Attribution rules" value={overviewMetric(usageAttributionRules.length)} />
            </div>

            <div className="mt-3 grid gap-3 xl:grid-cols-2">
              <div className="min-w-0 rounded-xl bg-background/60 p-3">
                <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 flex-[1_1_12rem]">
                    <p className="text-[10px] font-semibold">Statistics sources</p>
                    <p className="mt-1 text-[8px] text-foreground/35">Only ACTIVE verified sources may ingest samples.</p>
                  </div>
                  <Badge className="max-w-full whitespace-normal bg-primary/[0.08] text-[8px] text-primary">READ ONLY</Badge>
                </div>
                <div className="mt-2 space-y-1.5">
                  {usageSources.slice(0, 8).map((source) => (
                    <ResponsiveStatusRow key={source.source_key}
                      title={source.display_name} detail={<span className="font-mono">{source.source_key}</span>}
                      status={source.lifecycle_state} statusClassName={tone(source.lifecycle_state)}
                      statusDetail={`${source.provider_key ?? "RUNYI"} · ${source.collection_mode}`} />
                  ))}
                </div>
              </div>

              <div className="min-w-0 rounded-xl bg-background/60 p-3">
                <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 flex-[1_1_12rem]">
                    <p className="text-[10px] font-semibold">Latest usage measurements</p>
                    <p className="mt-1 text-[8px] text-foreground/35">Current Control Tower window; high-volume history remains paged and rollup-driven.</p>
                  </div>
                  <Badge className="max-w-full whitespace-normal break-words bg-foreground/[0.06] text-[8px] text-foreground/50">{usageRetentionPolicies[0]?.policy_key ?? "NO POLICY"}</Badge>
                </div>
                <div className="mt-2 space-y-1.5">
                  {overviewReady && usageSamples.length === 0 && <Empty text="No infrastructure usage samples have been collected yet." />}
                  {usageSamples.slice(0, 12).map((sample) => {
                    const series = usageSeries.find((item) => item.series_key === sample.series_key);
                    const metric = usageMetricDefinitions.find((item) => item.metric_key === series?.metric_key);
                    const unit = metric?.canonical_unit ?? "";
                    const value = sample.numeric_value != null
                      ? Number(sample.numeric_value).toLocaleString(undefined, { maximumFractionDigits: 2 })
                      : sample.text_value ?? "JSON";
                    return (
                      <ResponsiveStatusRow key={sample.sample_key}
                        title={metric?.display_name ?? series?.metric_key ?? sample.series_key}
                        detail={`${series?.provider_key ?? "RUNYI"} · ${series?.target_key ?? "UNKNOWN"} · ${new Date(sample.observed_at).toLocaleString()}`}
                        status={sample.quality_state} statusClassName={tone(sample.quality_state)}
                        statusDetail={`${value} ${unit}`} />
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="mt-3 rounded-lg bg-primary/[0.045] px-3 py-2 text-[9px] leading-5 text-foreground/50">
              Scale model: unbounded by fixed catalogue/schema counts, physically capacity-bound. Raw telemetry and long-term statistics are separated so RUNYI can retain compact rollups or archive history instead of turning Owner Root into an infinite log sink. Automatic deletion remains disabled until an archive binding is explicitly configured.
            </div>
          </div>

          <div className="mt-3 rounded-xl border border-foreground/8 bg-foreground/[0.02] p-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-2.5">
                <div className="grid size-8 shrink-0 place-items-center rounded-xl bg-primary/[0.08] text-primary">
                  <Activity className="size-4" />
                </div>
                <div>
                  <p className="text-xs font-semibold">Universal Provider Evidence Ingestion</p>
                  <p className="mt-1 text-[10px] leading-5 text-foreground/45">
                    Internal trusted sources ingest directly. External sources activate only after a real probe succeeds. Cloudflare public R2/D1 pricing is ACTIVE as raw verified catalog evidence; GraphQL, BigQuery and Supabase observability remain PREPARABLE until their exact access verifies.
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => void ingestInternalEvidence()}
                disabled={busy === "evidence_ingest"}
              >
                {busy === "evidence_ingest" ? <LoaderCircle className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
                Ingest internal evidence
              </Button>
            </div>

            <Input
              value={externalEvidenceApprovalId}
              onChange={(e) => setExternalEvidenceApprovalId(e.target.value)}
              className="mt-3 font-mono text-xs"
              placeholder="Bound Owner approval UUID for external source activation"
            />

            <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
              {evidenceSources.map((source) => {
                const latestActivation = evidenceActivationAttempts.find((item) => item.source_key === source.source_key);
                const sourceCatalogEntries = evidenceCatalogEntries.filter((item) => item.source_key === source.source_key);
                return (
                <div key={source.source_id} className="rounded-lg bg-background/60 p-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-[10px] font-semibold">{source.display_name}</p>
                      <p className="mt-1 truncate font-mono text-[8px] text-foreground/30">{source.source_key}</p>
                    </div>
                    <Badge className={tone(source.lifecycle_state)}>{source.lifecycle_state}</Badge>
                  </div>
                  <p className="mt-2 text-[9px] text-foreground/42">
                    {source.provider_key ?? "RUNYI"} · {source.source_class.replaceAll("_", " ")}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(source.supported_criteria ?? []).map((criterion) => (
                      <Badge key={criterion} className="bg-foreground/[0.05] text-[8px] text-foreground/50">
                        {criterion}
                      </Badge>
                    ))}
                    {source.auto_promote && (
                      <Badge className="bg-primary/[0.08] text-[8px] text-primary">AUTO PROMOTE</Badge>
                    )}
                  </div>
                  <p className="mt-2 text-[8px] text-foreground/30">
                    {source.last_success_at ? `Last success ${new Date(source.last_success_at).toLocaleString()}` : "No successful ingestion yet"}
                  </p>
                  {latestActivation && (
                    <p className="mt-1 text-[8px] text-foreground/35">
                      Activation: {latestActivation.status} · {latestActivation.probe_type.replaceAll("_", " ")}
                    </p>
                  )}
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {source.lifecycle_state === "PREPARABLE" && source.source_class !== "INTERNAL_SQL" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void activateExternalEvidence(source.source_key)}
                        disabled={busy === "external_evidence_activation" || !externalEvidenceApprovalId.trim()}
                      >
                        {busy === "external_evidence_activation" ? <LoaderCircle className="size-3 animate-spin" /> : <Activity className="size-3" />}
                        Verify & activate
                      </Button>
                    )}
                    {source.lifecycle_state === "ACTIVE" && source.source_class === "PUBLIC_CATALOG" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void ingestExternalCatalog(source.source_key)}
                        disabled={busy === "external_catalog_ingest"}
                      >
                        {busy === "external_catalog_ingest" ? <LoaderCircle className="size-3 animate-spin" /> : <RefreshCw className="size-3" />}
                        Ingest catalog
                      </Button>
                    )}
                    {sourceCatalogEntries.length > 0 && (
                      <Badge className="bg-primary/[0.08] text-[8px] text-primary">
                        {sourceCatalogEntries.length} catalog entries
                      </Badge>
                    )}
                  </div>
                </div>
                );
              })}
            </div>

            <div className="mt-3 grid gap-2 sm:grid-cols-4">
              <Metric label="Raw observations" value={overviewMetric(evidenceObservations.length)} />
              <Metric label="Promoted observations" value={overviewMetric(evidenceObservations.filter((item) => item.promoted).length)} />
              <Metric label="Recent ingestion runs" value={overviewMetric(evidenceRuns.length)} />
              <Metric label="Activation attempts" value={overviewMetric(evidenceActivationAttempts.length)} />
            </div>
          </div>

          <div className="mt-3 grid gap-3 xl:grid-cols-2">
            <div className="rounded-xl bg-foreground/[0.025] p-3">
              <p className="text-xs font-semibold">Register resource pool</p>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                <Input value={poolName} onChange={(e) => {
                  setPoolName(e.target.value);
                  if (!poolKey.trim()) setPoolKey(e.target.value.toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_+|_+$/g, ""));
                }} placeholder="Pool name" />
                <Input value={poolKey} onChange={(e) => setPoolKey(e.target.value.toUpperCase())} placeholder="POOL_KEY" className="font-mono" />
                <Input value={poolResourceType} onChange={(e) => setPoolResourceType(e.target.value.toUpperCase())} placeholder="Resource type" className="font-mono" />
                <Input value={poolProvider} onChange={(e) => setPoolProvider(e.target.value.toUpperCase())} placeholder="Provider (optional)" className="font-mono" />
                <Select value={poolEnvironment} onValueChange={setPoolEnvironment}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ANY">Any environment</SelectItem>
                    {["DEV", "STAGING", "CANARY", "PRODUCTION"].map((env) => <SelectItem key={env} value={env}>{env}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={poolAllocationPolicy} onValueChange={setPoolAllocationPolicy}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["SHARED_ALLOWED", "SHARED_REQUIRED", "OWNER_CHOICE"].map((value) => (
                      <SelectItem key={value} value={value}>{value.replaceAll("_", " ")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Textarea value={poolCapacityPolicy} onChange={(e) => setPoolCapacityPolicy(e.target.value)} className="mt-2 min-h-20 font-mono text-[11px]" placeholder="Capacity policy JSON" />
              <Input value={poolApprovalId} onChange={(e) => setPoolApprovalId(e.target.value)} className="mt-2 font-mono text-xs" placeholder="Bound resource-pool approval UUID" />
              <Button className="mt-2" onClick={() => void registerPool()} disabled={busy === "pool_register" || !poolKey.trim() || !poolName.trim()}>
                {busy === "pool_register" ? <LoaderCircle className="size-4 animate-spin" /> : <Plus className="size-4" />}
                Register pool
              </Button>
            </div>

            <div className="rounded-xl bg-foreground/[0.025] p-3">
              <p className="text-xs font-semibold">Add existing resource to pool</p>
              <div className="mt-2 grid gap-2">
                <Select value={memberPoolKey} onValueChange={setMemberPoolKey}>
                  <SelectTrigger><SelectValue placeholder="Choose pool" /></SelectTrigger>
                  <SelectContent>
                    {pools.filter((pool) => pool.lifecycle_state === "ACTIVE").map((pool) => (
                      <SelectItem key={pool.pool_id} value={pool.pool_key}>{pool.display_name} · {pool.resource_type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={memberResourceKey} onValueChange={setMemberResourceKey}>
                  <SelectTrigger><SelectValue placeholder="Choose installed resource" /></SelectTrigger>
                  <SelectContent>
                    {resources.map((resource) => (
                      <SelectItem key={resource.resource_key} value={resource.resource_key}>
                        {resource.display_name} · {resource.resource_type} · {resource.provider_key ?? "RUNYI"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input value={memberApprovalId} onChange={(e) => setMemberApprovalId(e.target.value)} className="font-mono text-xs" placeholder="Bound resource-pool approval UUID" />
                <Button variant="outline" onClick={() => void addPoolMember()} disabled={busy === "pool_member" || !memberPoolKey || !memberResourceKey}>
                  {busy === "pool_member" ? <LoaderCircle className="size-4 animate-spin" /> : <Plus className="size-4" />}
                  Add resource
                </Button>
              </div>
            </div>
          </div>

          <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {pools.map((pool) => {
              const poolMembers = members.filter((item) => item.pool_id === pool.pool_id && item.member_state === "ACTIVE");
              return (
                <div key={pool.pool_id} className="rounded-xl border border-foreground/8 bg-background/55 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-xs font-semibold">{pool.display_name}</p>
                      <p className="mt-1 font-mono text-[9px] text-foreground/35">{pool.pool_key}</p>
                    </div>
                    <Badge className={tone(pool.health_state)}>{pool.health_state}</Badge>
                  </div>
                  <p className="mt-2 text-[10px] text-foreground/45">
                    {pool.resource_type} · {pool.provider_key ?? "ANY PROVIDER"} · {pool.environment_key ?? "ANY ENVIRONMENT"}
                  </p>
                  <div className="mt-2 flex gap-1.5">
                    <Badge className="bg-foreground/[0.05] text-foreground/55">{pool.allocation_policy.replaceAll("_", " ")}</Badge>
                    <Badge className="bg-primary/[0.08] text-primary">{poolMembers.length} resources</Badge>
                  </div>
                </div>
              );
            })}
          </div>
          {notice && <Notice text={notice} />}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="border-foreground/8 bg-card py-0">
        <CardContent className="p-4">
          <Header
            icon={Sparkles}
            title="Universal Infrastructure Optimizer"
            description="Compare eligible installed resources, pools and executable provider profiles using workload objectives and trusted evidence. Recommendations are advisory; the Owner remains the final selector."
          />

          <div className="mt-3 rounded-xl bg-primary/[0.045] p-3 text-[10px] leading-5 text-foreground/55">
            RUNYI never invents missing cost or performance metrics. v1.16 can project monthly provider cost from verified price catalogs only when the workload supplies explicit usage. COST affects ranking only when an explicit target/ceiling envelope is supplied; shared provider free tiers are applied only through catalog_allowance_fraction.
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Metric label="Cost models" value={overviewMetric(infrastructureCostModels.length)} />
            <Metric label="Cost components" value={overviewMetric(infrastructureCostModelComponents.length)} />
            <Metric label="Verified catalog entries" value={overviewMetric(evidenceCatalogEntries.length)} />
            <Metric label="Cost-enabled providers" value={overviewMetric(new Set(infrastructureCostModels.map((item) => item.provider_key)).size)} />
          </div>

          <div className="mt-3 grid gap-3 lg:grid-cols-3">
            <FieldJson label="Workload profile" value={workloadProfile} setValue={(value) => { setWorkloadProfile(value); setRecommendation(null); }} />
            <FieldJson label="Objective weights" value={objectiveWeights} setValue={(value) => { setObjectiveWeights(value); setRecommendation(null); }} />
            <FieldJson label="Hard constraints" value={optimizationConstraints} setValue={(value) => { setOptimizationConstraints(value); setRecommendation(null); }} />
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => void previewRecommendations()}
              disabled={busy === "recommendation_preview" || !applicationEntityKey}
            >
              {busy === "recommendation_preview" ? <LoaderCircle className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
              Recommend infrastructure
            </Button>
            <Button
              variant="outline"
              onClick={() => void saveRecommendationSnapshot()}
              disabled={busy === "recommendation_snapshot" || !applicationEntityKey}
            >
              {busy === "recommendation_snapshot" ? <LoaderCircle className="size-4 animate-spin" /> : <Plus className="size-4" />}
              Save snapshot
            </Button>
            <Button
              onClick={useRecommendationsInDesigner}
              disabled={!recommendation || recommendation.readiness !== "READY"}
            >
              Use in designer
            </Button>
            {recommendation && (
              <>
                <Badge className={tone(recommendation.readiness)}>{recommendation.readiness}</Badge>
                <Badge className={tone(recommendation.evidence_quality)}>
                  {recommendation.evidence_quality} EVIDENCE · {Number(recommendation.evidence_coverage_pct).toFixed(0)}%
                </Badge>
              </>
            )}
          </div>

          {recommendation && (
            <div className="mt-3 space-y-3">
              {recommendation.missing_criteria?.length > 0 && (
                <p className="rounded-lg bg-amber-400/[0.06] px-3 py-2 text-[10px] text-foreground/55">
                  Missing or insufficient evidence: {recommendation.missing_criteria.join(", ")}. These dimensions are not fabricated or silently estimated.
                </p>
              )}
              <div className="grid gap-2 lg:grid-cols-3">
                {recommendation.recommendations.map((slot) => (
                  <div key={slot.slot} className="rounded-xl border border-foreground/8 bg-background/55 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-[10px] font-semibold">{slot.slot.replaceAll("_", " ")}</p>
                        <p className="mt-1 text-[9px] text-foreground/35">{slot.logical_capability ?? slot.resource_type ?? "Capability"}</p>
                      </div>
                      <Badge className={slot.recommendation ? tone("READY") : tone("BLOCKED")}>
                        {slot.candidate_count} OPTIONS
                      </Badge>
                    </div>

                    {slot.recommendation ? (
                      <div className="mt-2 rounded-lg bg-primary/[0.045] p-2.5">
                        <p className="text-[10px] font-semibold">{slot.recommendation.display_name ?? slot.recommendation.candidate_key}</p>
                        <p className="mt-1 text-[9px] text-foreground/42">
                          {slot.recommendation.provider_key ?? "RUNYI"} · {slot.recommendation.candidate_kind?.replaceAll("_", " ")}
                        </p>
                        <div className="mt-2 grid grid-cols-3 gap-1.5 text-center">
                          <MiniScore label="Evidence score" value={slot.recommendation.evidence_adjusted_score} />
                          <MiniScore label="Coverage" value={slot.recommendation.evidence_coverage_pct} suffix="%" />
                          <MiniScore label="Confidence" value={(slot.recommendation.confidence ?? 0) * 100} suffix="%" />
                        </div>
                        {slot.recommendation.cost_projection && (
                          <div className="mt-2 rounded-lg bg-foreground/[0.035] p-2">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-[9px] font-semibold">Workload cost projection</p>
                              <Badge className={tone(slot.recommendation.cost_projection.status)}>
                                {slot.recommendation.cost_projection.status}
                              </Badge>
                            </div>
                            {slot.recommendation.cost_projection.projected_monthly_cost != null && (
                              <p className="mt-1 text-[11px] font-semibold">
                                {slot.recommendation.cost_projection.currency ?? "USD"} {Number(slot.recommendation.cost_projection.projected_monthly_cost).toFixed(2)} / month
                              </p>
                            )}
                            {slot.recommendation.cost_projection.gross_list_rate_monthly_cost != null && (
                              <p className="mt-1 text-[8px] text-foreground/38">
                                Gross list rate {slot.recommendation.cost_projection.currency ?? "USD"} {Number(slot.recommendation.cost_projection.gross_list_rate_monthly_cost).toFixed(2)}
                                {slot.recommendation.cost_projection.allocated_allowance_savings != null
                                  ? ` · allocated allowance savings ${Number(slot.recommendation.cost_projection.allocated_allowance_savings).toFixed(2)}`
                                  : ""}
                              </p>
                            )}
                            {(slot.recommendation.cost_projection.missing_inputs?.length ?? 0) > 0 && (
                              <p className="mt-1 text-[8px] text-amber-600 dark:text-amber-300">
                                Missing inputs: {slot.recommendation.cost_projection.missing_inputs?.join(", ")}
                              </p>
                            )}
                            <p className="mt-1 font-mono text-[7px] text-foreground/28">
                              {slot.recommendation.cost_projection.model_key ?? "No applicable cost model"}
                            </p>
                          </div>
                        )}
                        <div className="mt-2 flex flex-wrap gap-1">
                          {Object.keys(slot.recommendation.evidence ?? {}).map((criterion) => (
                            <Badge key={criterion} className="bg-foreground/[0.05] text-[8px] text-foreground/50">
                              {criterion.replaceAll("_", " ")}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="mt-2 text-[10px] text-red-500">No eligible candidate currently satisfies this required slot.</p>
                    )}

                    {slot.candidates.length > 1 && (
                      <div className="mt-2 space-y-1">
                        {slot.candidates.slice(1, 4).map((candidate) => (
                          <div key={candidate.candidate_key} className="flex items-center justify-between gap-2 rounded-lg bg-foreground/[0.025] px-2 py-1.5">
                            <span className="truncate text-[9px] text-foreground/48">{candidate.display_name ?? candidate.candidate_key}</span>
                            <span className="shrink-0 text-right text-[9px] font-semibold">
                              {candidate.cost_projection?.projected_monthly_cost != null
                                ? `${candidate.cost_projection.currency ?? "USD"} ${Number(candidate.cost_projection.projected_monthly_cost).toFixed(2)} · `
                                : ""}
                              {Number(candidate.evidence_adjusted_score ?? 0).toFixed(1)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {notice && <Notice text={notice} />}
        </CardContent>
      </Card>

      <Card className="border-foreground/8 bg-card py-0">
        <CardContent className="p-4">
          <Header
            icon={Boxes}
            title="Infrastructure Composition Designer"
            description="Declare application capabilities once. RUNYI resolves Owner choices, current assignments, resource pools, installed resources and executable provider profiles."
          />

        <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <Select value={applicationEntityKey} onValueChange={(value) => { setApplicationEntityKey(value); setPreview(null); setRecommendation(null); }}>
            <SelectTrigger><SelectValue placeholder="Choose application" /></SelectTrigger>
            <SelectContent>
              {applications.map((app) => <SelectItem key={app.entity_key} value={app.entity_key}>{app.display_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={environmentKey} onValueChange={(value) => { setEnvironmentKey(value); setPreview(null); setRecommendation(null); }}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {["DEV", "STAGING", "CANARY", "PRODUCTION"].map((env) => <SelectItem key={env} value={env}>{env}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input value={compositionApprovalId} onChange={(e) => setCompositionApprovalId(e.target.value)} placeholder="Composition approval UUID" className="font-mono text-xs" />
          <Input value={materializeApprovalId} onChange={(e) => setMaterializeApprovalId(e.target.value)} placeholder="Materialize-to-Flow approval UUID" className="font-mono text-xs" />
        </div>

        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          <div>
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-foreground/38">Capability requirements</p>
            <Textarea value={requirements} onChange={(e) => { setRequirements(e.target.value); setPreview(null); }} className="min-h-64 font-mono text-[11px]" />
          </div>
          <div className="grid gap-2">
            <FieldJson label="Provider preferences by slot" value={providerPreferences} setValue={(value) => { setProviderPreferences(value); setPreview(null); }} />
            <FieldJson label="Existing resource preferences by slot" value={resourcePreferences} setValue={(value) => { setResourcePreferences(value); setPreview(null); }} />
            <FieldJson label="Policy / capacity / region constraints" value={policyConstraints} setValue={(value) => { setPolicyConstraints(value); setPreview(null); }} />
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => void previewComposition()} disabled={busy === "composition_preview" || !applicationEntityKey}>
            {busy === "composition_preview" ? <LoaderCircle className="size-4 animate-spin" /> : <Database className="size-4" />}
            Preview resolution
          </Button>
          <Button onClick={() => void publishComposition()} disabled={busy === "composition_publish" || !applicationEntityKey || preview?.readiness === "BLOCKED"}>
            {busy === "composition_publish" ? <LoaderCircle className="size-4 animate-spin" /> : <Plus className="size-4" />}
            Publish composition
          </Button>
          {preview && <Badge className={tone(preview.readiness)}>PREVIEW {preview.readiness}</Badge>}
        </div>

        {preview && (
          <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {preview.resolution.map((item, index) => (
              <div key={String(item.slot ?? index)} className="rounded-xl border border-foreground/8 bg-background/55 p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10px] font-semibold">{String(item.slot ?? "SLOT").replaceAll("_", " ")}</p>
                  <Badge className={tone(item.status)}>{item.status ?? "UNKNOWN"}</Badge>
                </div>
                <p className="mt-1 text-[10px] text-foreground/45">{item.provider_key ?? "Provider neutral"} · {item.resource_type ?? "RESOURCE"}</p>
                <p className="mt-1 truncate font-mono text-[9px] text-foreground/32">
                  {item.resource_key ?? item.pool_key ?? item.blueprint_key ?? item.profile_key ?? item.reason ?? ""}
                </p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 rounded-xl bg-foreground/[0.025] p-3">
          <div className="flex items-center gap-2">
            <Workflow className="size-4 text-primary" />
            <p className="text-xs font-semibold">Published compositions → Bridge / Flow</p>
          </div>
          <p className="mt-1 text-[10px] leading-5 text-foreground/45">
            Materialization creates ACTIVE assignments for reusable installed resources and DESIRED assignments for resources that still require provisioning. Provider execution remains in Flow.
          </p>
          <div className="mt-3 space-y-2">
            {compositions.filter((item) => !applicationEntityKey || item.application_entity_key === applicationEntityKey).slice(0, 12).map((composition) => (
              <div key={composition.composition_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-background/60 p-2.5">
                <div>
                  <p className="text-[10px] font-semibold">{composition.composition_key}</p>
                  <p className="mt-0.5 text-[9px] text-foreground/35">r{composition.revision} · {composition.environment_key}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <Badge className={tone(composition.state)}>{composition.state}</Badge>
                  <Badge className={tone(composition.readiness)}>{composition.readiness}</Badge>
                  <Button size="sm" variant="outline" onClick={() => void materializeComposition(composition)} disabled={busy === "composition_materialize" || composition.readiness !== "READY"}>
                    Send to Flow
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

          {notice && <Notice text={notice} />}
        </CardContent>
      </Card>
    </div>
  );
}

function MiniScore({
  label,
  value,
  suffix = "",
}: {
  label: string;
  value?: number;
  suffix?: string;
}) {
  return (
    <div className="rounded-lg bg-background/70 px-1.5 py-1.5">
      <p className="text-[8px] text-foreground/32">{label}</p>
      <p className="mt-0.5 text-[10px] font-semibold">{Number(value ?? 0).toFixed(0)}{suffix}</p>
    </div>
  );
}

function Header({
  icon: Icon,
  title,
  description,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="grid size-8 shrink-0 place-items-center rounded-xl bg-primary/[0.08] text-primary">
        <Icon className="size-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{title}</p>
        <p className="mt-1 text-[11px] leading-5 text-foreground/58">{description}</p>
      </div>
    </div>
  );
}

function FieldJson({
  label,
  value,
  setValue,
}: {
  label: string;
  value: string;
  setValue: (value: string) => void;
}) {
  return (
    <div>
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-foreground/38">{label}</p>
      <Textarea value={value} onChange={(e) => setValue(e.target.value)} className="min-h-20 font-mono text-[11px]" />
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-xl bg-foreground/[0.025] px-2.5 py-2">
      <p className="truncate text-[8px] uppercase tracking-wider text-foreground/45">{label}</p>
      <p className="mt-0.5 text-sm font-semibold">{value}</p>
    </div>
  );
}

function MobileFact({ label, children, wide = false }: { label: string; children: ReactNode; wide?: boolean }) {
  return (
    <div className={`min-w-0 rounded-lg bg-foreground/[0.025] px-2.5 py-2 ${wide ? "col-span-2" : ""}`}>
      <p className="mb-1 text-[7px] font-semibold uppercase tracking-wider text-foreground/45">{label}</p>
      <div className="min-w-0 text-foreground/70">{children}</div>
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return <p className="rounded-xl bg-foreground/[0.025] px-3 py-5 text-center text-xs text-foreground/50">{text}</p>;
}

function Notice({ text }: { text: string }) {
  return <p className="mt-3 rounded-xl bg-primary/[0.06] px-3 py-2 text-[11px] leading-5 text-foreground/65">{text}</p>;
}
